import React, { useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import DOMPurify from 'dompurify'
import parse from 'html-react-parser'
import { openDocumentInTab, clearDocumentTabsTracking,applyHighlighting } from './tabManager'

// Improved highlighting function
export const highlightText = (text, searchQuery) => {
  if (!searchQuery || typeof text !== 'string') return text

  // Escape special characters in the search query
  const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  // Create a regex that can match the search term
  const regex = new RegExp(`(${escapedQuery})`, 'gi')

  // Split HTML into parts inside tags and text content
  const parts = []
  let inTag = false
  let currentPart = ''

  for (let i = 0; i < text.length; i++) {
    if (text[i] === '<') {
      if (currentPart) {
        parts.push({ isTag: inTag, content: currentPart })
        currentPart = ''
      }
      inTag = true
      currentPart += text[i]
    } else if (text[i] === '>') {
      currentPart += text[i]
      parts.push({ isTag: inTag, content: currentPart })
      currentPart = ''
      inTag = false
    } else {
      currentPart += text[i]
    }
  }

  if (currentPart) {
    parts.push({ isTag: inTag, content: currentPart })
  }

  // Only highlight text parts, not tag parts
  return parts
    .map((part) => {
      if (part.isTag) return part.content
      return part.content.replace(regex, '<mark>$1</mark>')
    })
    .join('')
}

// Helper function to parse child nodes, especially handling <mark> tags
const parseChildNode = (child, index) => {
  if (child?.name === 'mark') {
    return (
      <mark key={index}>
        {child?.children?.map((subChild) => subChild?.data).join('')}
      </mark>
    )
  }
  return child?.data || ''
}

// Helper function to check if a URL is absolute
const isAbsoluteUrl = (url) => {
  return /^(https?:\/\/|\/\/|[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,})/i.test(
    url
  )
}

// Helper function to check if URL is external
const isExternalUrl = (url) => {
  if (!url.startsWith('http')) return false
  try {
    const urlObj = new URL(url)
    return urlObj.hostname !== window.location.hostname
  } catch {
    return false
  }
}

// Helper function to check if link is a special bookmark/note link
const isSpecialBookmarkLink = (href) => {
  if (!href || !href.startsWith('#')) return false

  const fragment = href.substring(1) // Remove the #

  // Check for bookmark patterns (include #bookmark, #bookmark0, #bookmark1, etc.)
  // But exclude #bookmarkback, #bookmarkback1, etc.
  if (fragment.startsWith('bookmark')) {
    // If it starts with 'bookmarkback', exclude it
    if (fragment.startsWith('bookmarkback')) {
      return false
    }
    // If it's exactly 'bookmark' or 'bookmark' followed by digits, include it
    const afterBookmark = fragment.substring(8) // 'bookmark'.length = 8
    if (afterBookmark === '' || /^\d+$/.test(afterBookmark)) {
      return true
    }
  }

  // Check for footnote patterns (include #footnote1, #footnote10, etc.)
  // But exclude #footnoteback, #footnoteback1, etc.
  if (fragment.startsWith('footnote')) {
    // If it starts with 'footnoteback', exclude it
    if (fragment.startsWith('footnoteback')) {
      return false
    }
    // If it's 'footnote' followed by digits, include it
    const afterFootnote = fragment.substring(8) // 'footnote'.length = 8
    if (/^\d+$/.test(afterFootnote)) {
      return true
    }
  }

  // Check for gtlnotes patterns (include #gtlnotes0, #gtlnotes1, etc.)
  // But exclude #gtlnotes (without numbers)
  if (fragment.startsWith('gtlnotes')) {
    const afterGtlnotes = fragment.substring(8) // 'gtlnotes'.length = 8
    // Only include if followed by digits (exclude bare 'gtlnotes')
    if (/^\d+$/.test(afterGtlnotes)) {
      return true
    }
  }

  return false
}

// Helper function to open URL in new tab with proper focus management
const openUrlWithFocusManagement = (url, linkText, showSnackbar) => {
  // console.log(`Utils: Opening URL with focus management: ${url}`)
  openDocumentInTab(url, linkText, showSnackbar)
}

// Enhanced CSS selector escaping function
const escapeSelector = (selector) => {
  return selector.replace(/[!"#$%&'()*+,.\/:;<=>?@\[\\\]^`{|}~]/g, '\\$&')
}

// Helper function to find element by ID with special characters
const findElementById = (container, targetId) => {
  // Method 1: Try direct getElementById (works in most cases)
  let targetElement = container.querySelector
    ? container.ownerDocument?.getElementById(targetId) ||
      document.getElementById(targetId)
    : document.getElementById(targetId)

  if (
    targetElement &&
    container.contains &&
    container.contains(targetElement)
  ) {
    return targetElement
  }

  // Method 2: Try escaped selector
  try {
    const escapedId = escapeSelector(targetId)
    targetElement = container.querySelector(`#${escapedId}`)
    if (targetElement) {
      return targetElement
    }
  } catch (e) {
    // console.warn(
    //   `Failed to query with escaped selector: #${escapeSelector(targetId)}`,
    //   e
    // )
    }

  // Method 3: Use attribute selector (most reliable for special characters)
  try {
    targetElement = container.querySelector(`[id="${targetId}"]`)
    if (targetElement) {
      return targetElement
    }
  } catch (e) {
    // console.warn(
    //   `Failed to query with attribute selector: [id="${targetId}"]`,
    //   e
    // )
  }

  // Method 4: Manual search through all elements with IDs
  const allElementsWithId = container.querySelectorAll('[id]')
  for (const element of allElementsWithId) {
    if (element.id === targetId) {
      return element
    }
  }

  // Method 5: Also check for name attribute (fallback for older HTML)
  try {
    targetElement = container.querySelector(`[name="${targetId}"]`)
    if (targetElement) {
      return targetElement
    }
  } catch (e) {
// console.warn(`Failed to query with name attribute: [name="${targetId}"]`, e)
  }

  return null
}

// Global popup state management
let popupManager = {
  mainPopup: null,
  nestedPopup: null,
  setMainPopup: null,
  setNestedPopup: null,
}

// ✅ React Portal Nested Popup Component
const NestedPopup = ({
  targetId,
  fullDocumentContent,
  documentTitle,
  showSnackbar,
  baseUrl,
  onClose,
}) => {
  const [showBackToTop, setShowBackToTop] = useState(false)
  const contentRef = React.useRef(null)
  // in guides, we are using #gtlnotes pattern for GTL Note, but in treaties we are using #footnote pattern
  const titleText = targetId.startsWith('#footnote')
    ? 'GTL Note'
    : targetId.startsWith('#gtlnotes')
    ? 'GTL Note'
    : 'Footnote'
  const cleanTargetId = targetId.startsWith('#')
    ? targetId.substring(1)
    : targetId

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', handleEsc, true)
    return () => document.removeEventListener('keydown', handleEsc, true)
  }, [onClose])

  // Handle scroll for back-to-top button
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        setShowBackToTop(contentRef.current.scrollTop > 300)
      }
    }

    const container = contentRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const scrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  // Find target content
  let targetContent = null
  if (fullDocumentContent) {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = fullDocumentContent
    const targetElement = findElementById(tempDiv, cleanTargetId)
    if (targetElement) {
      targetContent = targetElement.outerHTML
    }
  }

  return createPortal(
    <div
      id='nested-popup-container'
      className='nested-popup-overlay'
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 15000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
        boxSizing: 'border-box',
      }}
      onClick={onClose}
    >
      <div
        className='nested-popup-container'
        style={{
          background: '#232536',
          borderRadius: '12px',
          maxWidth: '85%',
          maxHeight: '85%',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          display: 'flex',
          flexDirection: 'column',
          border: '3px solid #fff',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className='nested-popup-header gtl-notes-accordion-summary'
          style={{
            padding: '16px 20px',
            borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            flexShrink: 0,
          }}
        >
          <h3
            className='nested-popup-title'
            style={{
              margin: 0,
              color: 'white',
              fontSize: '1.1rem',
              fontWeight: 600,
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            }}
          >
            {titleText}
          </h3>
          <button
            className='nested-popup-close-button'
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              fontSize: '28px',
              cursor: 'pointer',
              color: 'white',
              padding: 0,
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.2s',
              backdropFilter: 'blur(10px)',
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div
          ref={contentRef}
          className='nested-popup-content gtl-notes-accordion-details'
          style={{
            padding: '20px',
            overflowY: 'auto',
            flex: 1,
            lineHeight: 1.6,
            background: 'white',
            color: '#333',
            position: 'relative',
          }}
        >
          {targetContent ? (
            <div className='gtl-notes-content'>
              <HtmlContentRenderer
                content={targetContent}
                fullDocumentContent={fullDocumentContent}
                documentTitle={documentTitle}
                showSnackbar={showSnackbar}
                baseUrl={baseUrl}
                isNestedPopup={true}
              />
            </div>
          ) : (
            <p style={{ color: '#666' }}>
              Content for section "{cleanTargetId}" not found.
            </p>
          )}

          {/* Back to top button */}
          {showBackToTop && (
            <button
              className='nested-popup-back-to-top'
              onClick={scrollToTop}
              style={{
                position: 'sticky',
                bottom: '20px',
                float: 'right',
                marginTop: '-60px',
                zIndex: 1400,
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#232536',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                transition: 'all 0.3s ease',
              }}
            >
              ↑
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

// ✅ React Portal Main Popup Component
const MainPopup = ({
  targetId,
  fullDocumentContent,
  documentTitle,
  showSnackbar,
  baseUrl,
  onClose,
}) => {
  const [showBackToTop, setShowBackToTop] = useState(false)
  const contentRef = React.useRef(null)

  const cleanTargetId = targetId.startsWith('#')
    ? targetId.substring(1)
    : targetId

  // Handle ESC key (only if no nested popup)
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && !popupManager.nestedPopup) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [onClose])

  // Handle scroll for back-to-top button
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        setShowBackToTop(contentRef.current.scrollTop > 300)
      }
    }

    const container = contentRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Scroll to target after render
  useEffect(() => {
    setTimeout(() => {
      if (contentRef.current && cleanTargetId) {
        const targetElement = findElementById(contentRef.current, cleanTargetId)
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
          // Use the consistent highlighting function
          const success = applyHighlighting(targetElement, 3000)
          if (success && showSnackbar) {
            showSnackbar(`Scrolled to section: ${cleanTargetId}`, 'success')
          }
        }
      }
    }, 100)
  }, [cleanTargetId, showSnackbar])

  const scrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const isMobile = window.innerWidth <= 768

  return createPortal(
    <div
      id='main-popup-container'
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 10000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
        boxSizing: 'border-box',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          maxWidth: '90%',
          maxHeight: '90%',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#f5f5f5',
            flexShrink: 0,
          }}
        >
          <h3
            style={{
              margin: 0,
              color: '#232536',
              fontSize: '1.1rem',
              fontWeight: 600,
            }}
          >
            {documentTitle.replace(/<[^>]*>/g, '') || 'Document Section'}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#666',
              padding: 0,
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'background-color 0.2s',
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div
          ref={contentRef}
          style={{
            padding: '20px',
            overflowY: 'auto',
            flex: 1,
            lineHeight: 1.6,
            position: 'relative',
          }}
        >
          <HtmlContentRenderer
            content={fullDocumentContent}
            fullDocumentContent={fullDocumentContent}
            documentTitle={documentTitle}
            showSnackbar={showSnackbar}
            baseUrl={baseUrl}
            isNestedPopup={false}
          />

          {/* Back to top button */}
          {showBackToTop && (
            <button
              onClick={scrollToTop}
              style={{
                position: 'sticky',
                bottom: '20px',
                float: 'right',
                marginTop: '-60px',
                zIndex: 1400,
                ...(isMobile
                  ? {
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      fontSize: '20px',
                    }
                  : {
                      padding: '8px 16px',
                      borderRadius: '28px',
                      fontSize: '14px',
                    }),
                backgroundColor: '#232536',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                transition: 'all 0.3s ease',
                gap: isMobile ? 0 : '8px',
              }}
            >
              {isMobile ? '↑' : '↑ Back to Top'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

// ✅ HTML Content Renderer Component
const HtmlContentRenderer = ({
  content,
  fullDocumentContent,
  documentTitle,
  showSnackbar,
  baseUrl,
  isNestedPopup = false,
}) => {
  // Helper function to handle navigation between popups
  const handlePopupNavigation = (
    targetId,
    fullDocumentContent,
    documentTitle,
    showSnackbar,
    baseUrl
  ) => {
    return new Promise((resolve) => {
      // Close nested popup first if it exists
      if (popupManager.nestedPopup) {
        popupManager.setNestedPopup(null)
      }

      // Small delay to ensure nested popup closes smoothly
      setTimeout(() => {
        // Check if main popup exists
        const mainPopupContainer = document.getElementById(
          'main-popup-container'
        )

        if (mainPopupContainer && popupManager.mainPopup) {
          // Main popup exists - try to navigate within it
          const mainPopupContent = mainPopupContainer.querySelector(
            '[data-popup-content]'
          )
          const targetInMain = findElementById(
            mainPopupContent || mainPopupContainer,
            targetId
          )

          if (targetInMain) {
            targetInMain.scrollIntoView({ behavior: 'smooth', block: 'start' })
            const success = applyHighlighting(targetInMain, 3000)

            if (success && showSnackbar) {
              showSnackbar(`Navigated to section: ${targetId}`, 'success')
            }
            resolve({ success: true, action: 'scrolled_in_main' })
          } else {
            // Target not found in main popup either
            if (showSnackbar) {
              showSnackbar(
                `Section "${targetId}" not found in document`,
                'warning'
              )
            }
            resolve({ success: false, action: 'not_found' })
          }
        } else {
          // No main popup - create one
          if (popupManager.setMainPopup) {
            popupManager.setMainPopup(
              <MainPopup
                targetId={targetId}
                fullDocumentContent={fullDocumentContent}
                documentTitle={documentTitle}
                showSnackbar={showSnackbar}
                baseUrl={baseUrl}
                onClose={() => popupManager.setMainPopup(null)}
              />
            )

            if (showSnackbar) {
              showSnackbar(
                `Opened document and navigated to: ${targetId}`,
                'info'
              )
            }
            resolve({ success: true, action: 'created_main_popup' })
          } else {
            resolve({ success: false, action: 'no_popup_manager' })
          }
        }
      }, 150) // Slightly longer delay for better UX
    })
  }
  
  const handleLinkClick = useCallback(
    async (e, href) => {
      // Handle special URL schemes
      if (
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href.startsWith('sms:')
      ) {
        if (showSnackbar) {
          const scheme = href.split(':')[0]
          showSnackbar(`Opening ${scheme} link`, 'info')
        }
        return
      }

      e.preventDefault()
      e.stopPropagation()

      if (href.startsWith('#')) {
        // Check if it's a special bookmark/note link
        if (isSpecialBookmarkLink(href)) {
          popupManager.setNestedPopup(
            <NestedPopup
              targetId={href}
              fullDocumentContent={fullDocumentContent}
              documentTitle={documentTitle}
              showSnackbar={showSnackbar}
              baseUrl={baseUrl}
              onClose={() => popupManager.setNestedPopup(null)}
            />
          )
          return
        }

        // Regular internal link handling
        const targetId = href.substring(1)
        const popup = e.target.closest(
          '#main-popup-container, #nested-popup-container'
        )

        if (popup) {
          // We're inside a popup
          const popupContent = popup.querySelector('[data-popup-content]')
          const targetElement = findElementById(popupContent || popup, targetId)

          if (targetElement) {
            // Target found in current popup
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
            const success = applyHighlighting(targetElement, 3000)
            if (success && showSnackbar) {
              showSnackbar(`Navigated to section: ${targetId}`, 'success')
            }
          } else {
            // Target not found - use the navigation helper
            const isNestedPopup = popup.id === 'nested-popup-container'

            if (isNestedPopup) {
              // Handle the cross-popup navigation
              await handlePopupNavigation(
                targetId,
                fullDocumentContent,
                documentTitle,
                showSnackbar,
                baseUrl
              )
            } else {
              // We're in main popup but target not found
              if (showSnackbar) {
                showSnackbar(`Section "${targetId}" not found`, 'warning')
              }
            }
          }
        } else {
          // Not in a popup - create main popup
          if (popupManager.setMainPopup) {
            popupManager.setMainPopup(
              <MainPopup
                targetId={targetId}
                fullDocumentContent={fullDocumentContent}
                documentTitle={documentTitle}
                showSnackbar={showSnackbar}
                baseUrl={baseUrl}
                onClose={() => popupManager.setMainPopup(null)}
              />
            )

            if (showSnackbar) {
              showSnackbar('Section opened in popup', 'info')
            }
          }
        }
      } else if (isExternalUrl(href)) {
        // Handle external links
        const linkText = e.target.textContent?.trim() || 'External Link'
        openUrlWithFocusManagement(href, linkText, showSnackbar)
      } else {
        // Handle internal document links
        let fullUrl
        if (href.startsWith('http') || href.startsWith('//')) {
          fullUrl = href
        } else if (href.startsWith('/')) {
          fullUrl = `${window.location.origin}${href}`
        } else {
          const currentBaseUrl = baseUrl || window.location.pathname
          const basePath = currentBaseUrl.substring(
            0,
            currentBaseUrl.lastIndexOf('/') + 1
          )
          fullUrl = `${window.location.origin}${basePath}${href}`
        }

        const linkText = e.target.textContent?.trim() || 'Document'
        openUrlWithFocusManagement(fullUrl, linkText, showSnackbar)
      }
    },
    [fullDocumentContent, documentTitle, showSnackbar, baseUrl]
  )

  // Sanitize and parse HTML
  const cleanHtml = DOMPurify.sanitize(content || '', {
    ADD_ATTR: ['target', 'rel', 'class', 'id', 'name', 'href'],
    ADD_TAGS: ['mark'],
  })
  const parseStyleString = (styleStr) => {
    if (!styleStr) return {}
    return styleStr.split(';').reduce((acc, style) => {
      const [key, value] = style.split(':').map((s) => s.trim())
      if (key && value) {
        // Convert kebab-case to camelCase for React
        const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase())
        acc[camelKey] = value
      }
      return acc
    }, {})
  }

  const parsedContent = parse(cleanHtml, {
    replace: (domNode) => {
      if (domNode.name === 'a' && domNode.attribs?.href) {
        const href = domNode.attribs.href
        const inlineStyle = parseStyleString(domNode.attribs.style)

        return (
          <a
            href={href}
            className={domNode.attribs.class}
            id={domNode.attribs.id}
            style={inlineStyle}
            onClick={(e) => handleLinkClick(e, href)}
            {...(isExternalUrl(href) &&
            !href.startsWith('mailto:') &&
            !href.startsWith('tel:') &&
            !href.startsWith('sms:')
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
          >
            {domNode.children?.map((child, index) =>
              parseChildNode(child, index)
            )}
          </a>
        )
      }
      return undefined
    },
  })

  return (
    <div className='html-content' data-popup-content>
      {parsedContent}
    </div>
  )
}

// ✅ Main Popup Manager Component
const PopupManager = () => {
  const [mainPopup, setMainPopup] = useState(null)
  const [nestedPopup, setNestedPopup] = useState(null)

  // Update global popup manager
  React.useEffect(() => {
    popupManager.mainPopup = mainPopup
    popupManager.nestedPopup = nestedPopup
    popupManager.setMainPopup = setMainPopup
    popupManager.setNestedPopup = setNestedPopup
  }, [mainPopup, nestedPopup])

  return (
    <>
      {mainPopup}
      {nestedPopup}
    </>
  )
}

// ✅ Hook to manage popups
export const usePopupManager = () => {
  const openMainPopup = useCallback(
    (targetId, fullDocumentContent, documentTitle, showSnackbar, baseUrl) => {
      if (popupManager.setMainPopup) {
        popupManager.setMainPopup(
          <MainPopup
            targetId={targetId}
            fullDocumentContent={fullDocumentContent}
            documentTitle={documentTitle}
            showSnackbar={showSnackbar}
            baseUrl={baseUrl}
            onClose={() => popupManager.setMainPopup(null)}
          />
        )
      }
    },
    []
  )

  const openNestedPopup = useCallback(
    (targetId, fullDocumentContent, documentTitle, showSnackbar, baseUrl) => {
      if (popupManager.setNestedPopup) {
        popupManager.setNestedPopup(
          <NestedPopup
            targetId={targetId}
            fullDocumentContent={fullDocumentContent}
            documentTitle={documentTitle}
            showSnackbar={showSnackbar}
            baseUrl={baseUrl}
            onClose={() => popupManager.setNestedPopup(null)}
          />
        )
      }
    },
    []
  )

  const closeAllPopups = useCallback(() => {
    if (popupManager.setMainPopup) popupManager.setMainPopup(null)
    if (popupManager.setNestedPopup) popupManager.setNestedPopup(null)
  }, [])

  return { openMainPopup, openNestedPopup, closeAllPopups }
}

/**
 * React component to render HTML content with proper link handling
 */
const HtmlContent = ({
  content,
  searchQuery,
  baseUrl,
  fullDocumentContent,
  documentTitle,
  showSnackbar,
}) => {
  // Apply highlighting to the content if searchQuery is provided
  const highlightedContent = searchQuery
    ? highlightText(content, searchQuery)
    : content

  // Handle hash fragment scrolling when the page loads
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash) {
      const timer = setTimeout(() => {
        const hash = window.location.hash
        if (hash.startsWith('#')) {
          const targetId = hash.substring(1)
          const targetElement = findElementById(document, targetId)

          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth' })
            // console.log(`Scrolled to element: ${targetId}`)
          } else {
            // console.warn(`Target element not found: ${targetId}`)
          }
        }
      }, 300)

      return () => clearTimeout(timer)
    }
  }, [])

  return (
    <div className='html-content'>
      <HtmlContentRenderer
        content={highlightedContent}
        fullDocumentContent={fullDocumentContent}
        documentTitle={documentTitle}
        showSnackbar={showSnackbar}
        baseUrl={baseUrl}
        isNestedPopup={false}
      />
      <PopupManager />
    </div>
  )
}

/**
 * Main export function that returns the HtmlContent component
 */
export const parseHtmlSanitizeAddTargetToLinks = (
  content,
  searchQuery,
  baseUrl,
  options = {}
) => {
  if (!content) return null

  const {
    fullDocumentContent = null,
    documentTitle = 'Document',
    showSnackbar = null,
  } = options

  return (
    <HtmlContent
      content={content}
      searchQuery={searchQuery}
      baseUrl={baseUrl}
      fullDocumentContent={fullDocumentContent}
      documentTitle={documentTitle}
      showSnackbar={showSnackbar}
    />
  )
}

// Maintain existing utility exports
export const deepCopy = (obj) => {
  return JSON.parse(JSON.stringify(obj))
}

export const debounce = (func, delay) => {
  let timeoutId
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      func(...args)
    }, delay)
  }
}

// Clean up function to clear opened tabs tracking
export const clearOpenedTabsTracking = () => {
  clearDocumentTabsTracking()
}

// Export the popup manager components
export { PopupManager }













// -----------------------------------------------------------------------------------------
// v3 Version 3 Fixes

// ✅ Copies all CSS files to document head for 2nd popup(gtlnotes and footnotes) use
// ✅ Maintains proper table formatting, typography, colors
// ✅ Preserves document-specific styling (like guide themes)
// ✅ Cleans up CSS when popups close (prevents memory leaks)
// ✅ Uses unique IDs to prevent CSS conflicts

// import React, { useEffect } from 'react'
// import DOMPurify from 'dompurify'
// import parse from 'html-react-parser'
// import { openDocumentInTab, clearDocumentTabsTracking } from './tabManager'

// // Improved highlighting function
// export const highlightText = (text, searchQuery) => {
//   if (!searchQuery || typeof text !== 'string') return text

//   // Escape special characters in the search query
//   const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

//   // Create a regex that can match the search term
//   const regex = new RegExp(`(${escapedQuery})`, 'gi')

//   // Split HTML into parts inside tags and text content
//   const parts = []
//   let inTag = false
//   let currentPart = ''

//   for (let i = 0; i < text.length; i++) {
//     if (text[i] === '<') {
//       if (currentPart) {
//         parts.push({ isTag: inTag, content: currentPart })
//         currentPart = ''
//       }
//       inTag = true
//       currentPart += text[i]
//     } else if (text[i] === '>') {
//       currentPart += text[i]
//       parts.push({ isTag: inTag, content: currentPart })
//       currentPart = ''
//       inTag = false
//     } else {
//       currentPart += text[i]
//     }
//   }

//   if (currentPart) {
//     parts.push({ isTag: inTag, content: currentPart })
//   }

//   // Only highlight text parts, not tag parts
//   return parts
//     .map((part) => {
//       if (part.isTag) return part.content
//       return part.content.replace(regex, '<mark>$1</mark>')
//     })
//     .join('')
// }

// // Helper function to parse child nodes, especially handling <mark> tags
// const parseChildNode = (child, index) => {
//   if (child?.name === 'mark') {
//     return (
//       <mark key={index}>
//         {child?.children?.map((subChild) => subChild?.data).join('')}
//       </mark>
//     )
//   }
//   return child?.data || ''
// }

// // Helper function to check if a URL is absolute
// const isAbsoluteUrl = (url) => {
//   return /^(https?:\/\/|\/\/|[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,})/i.test(
//     url
//   )
// }

// // Helper function to check if URL is external
// const isExternalUrl = (url) => {
//   if (!url.startsWith('http')) return false
//   try {
//     const urlObj = new URL(url)
//     return urlObj.hostname !== window.location.hostname
//   } catch {
//     return false
//   }
// }

// // ✅ NEW: Helper function to check if link is a special bookmark/note link
// const isSpecialBookmarkLink = (href) => {
//   if (!href || !href.startsWith('#')) return false

//   const fragment = href.substring(1) // Remove the #

//   // Check for bookmark patterns (include #bookmark, #bookmark0, #bookmark1, etc.)
//   // But exclude #bookmarkback, #bookmarkback1, etc.
//   if (fragment.startsWith('bookmark')) {
//     // If it starts with 'bookmarkback', exclude it
//     if (fragment.startsWith('bookmarkback')) {
//       return false
//     }
//     // If it's exactly 'bookmark' or 'bookmark' followed by digits, include it
//     const afterBookmark = fragment.substring(8) // 'bookmark'.length = 8
//     if (afterBookmark === '' || /^\d+$/.test(afterBookmark)) {
//       return true
//     }
//   }

//   // Check for footnote patterns (include #footnote1, #footnote10, etc.)
//   // But exclude #footnoteback, #footnoteback1, etc.
//   if (fragment.startsWith('footnote')) {
//     // If it starts with 'footnoteback', exclude it
//     if (fragment.startsWith('footnoteback')) {
//       return false
//     }
//     // If it's 'footnote' followed by digits, include it
//     const afterFootnote = fragment.substring(8) // 'footnote'.length = 8
//     if (/^\d+$/.test(afterFootnote)) {
//       return true
//     }
//   }

//   // Check for gtlnotes patterns (include #gtlnotes0, #gtlnotes1, etc.)
//   // But exclude #gtlnotes (without numbers)
//   if (fragment.startsWith('gtlnotes')) {
//     const afterGtlnotes = fragment.substring(8) // 'gtlnotes'.length = 8
//     // Only include if followed by digits (exclude bare 'gtlnotes')
//     if (/^\d+$/.test(afterGtlnotes)) {
//       return true
//     }
//   }

//   return false
// }

// // Helper function to open URL in new tab with proper focus management
// const openUrlWithFocusManagement = (url, linkText, showSnackbar) => {
//   console.log(`Utils: Opening URL with focus management: ${url}`)
//   openDocumentInTab(url, linkText, showSnackbar)
// }

// // Enhanced CSS selector escaping function
// const escapeSelector = (selector) => {
//   return selector.replace(/[!"#$%&'()*+,.\/:;<=>?@\[\\\]^`{|}~]/g, '\\$&')
// }

// // Helper function to find element by ID with special characters
// const findElementById = (container, targetId) => {
//   // Method 1: Try direct getElementById (works in most cases)
//   let targetElement = container.querySelector
//     ? container.ownerDocument?.getElementById(targetId) ||
//       document.getElementById(targetId)
//     : document.getElementById(targetId)

//   if (
//     targetElement &&
//     container.contains &&
//     container.contains(targetElement)
//   ) {
//     return targetElement
//   }

//   // Method 2: Try escaped selector
//   try {
//     const escapedId = escapeSelector(targetId)
//     targetElement = container.querySelector(`#${escapedId}`)
//     if (targetElement) {
//       return targetElement
//     }
//   } catch (e) {
//     console.warn(
//       `Failed to query with escaped selector: #${escapeSelector(targetId)}`,
//       e
//     )
//   }

//   // Method 3: Use attribute selector (most reliable for special characters)
//   try {
//     targetElement = container.querySelector(`[id="${targetId}"]`)
//     if (targetElement) {
//       return targetElement
//     }
//   } catch (e) {
//     console.warn(
//       `Failed to query with attribute selector: [id="${targetId}"]`,
//       e
//     )
//   }

//   // Method 4: Manual search through all elements with IDs
//   const allElementsWithId = container.querySelectorAll('[id]')
//   for (const element of allElementsWithId) {
//     if (element.id === targetId) {
//       return element
//     }
//   }

//   // Method 5: Also check for name attribute (fallback for older HTML)
//   try {
//     targetElement = container.querySelector(`[name="${targetId}"]`)
//     if (targetElement) {
//       return targetElement
//     }
//   } catch (e) {
//     console.warn(`Failed to query with name attribute: [name="${targetId}"]`, e)
//   }

//   return null
// }

// // ✅ NEW: Create nested popup for special bookmarks/notes (appears on top of existing popup)
// const createNestedPopup = (
//   targetId,
//   fullDocumentContent,
//   documentTitle,
//   showSnackbar,
//   baseUrl = null
// ) => {
//   // Remove existing nested popup if any
//   const existingNestedPopup = document.getElementById('nested-content-popup')
//   if (existingNestedPopup) {
//     existingNestedPopup.remove()
//   }

//   // Create nested popup overlay with higher z-index
//   const nestedOverlay = document.createElement('div')
//   nestedOverlay.id = 'nested-content-popup'
//   nestedOverlay.style.cssText = `
//     position: fixed;
//     top: 0;
//     left: 0;
//     width: 100%;
//     height: 100%;
//     background-color: rgba(0, 0, 0, 0.8);
//     z-index: 15000;
//     display: flex;
//     justify-content: center;
//     align-items: center;
//     padding: 20px;
//     box-sizing: border-box;
//   `

//   // Create nested popup content container with distinct styling
//   const nestedPopup = document.createElement('div')
//   nestedPopup.style.cssText = `
//     background: #232536;
//     border-radius: 12px;
//     max-width: 85%;
//     max-height: 85%;
//     overflow: hidden;
//     position: relative;
//     box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
//     display: flex;
//     flex-direction: column;
//     border: 3px solid #fff;
//   `

//   // Create header with distinct styling for nested popup
//   const nestedHeader = document.createElement('div')
//   nestedHeader.style.cssText = `
//     padding: 16px 20px;
//     border-bottom: 2px solid rgba(255, 255, 255, 0.2);
//     display: flex;
//     justify-content: space-between;
//     align-items: center;
//     background: rgba(255, 255, 255, 0.05);
//     backdrop-filter: blur(10px);
//     flex-shrink: 0;
//   `

//   const nestedTitle = document.createElement('h3')
//   const titleText = targetId.startsWith('gtlNotes') ? 'GTL Notes' : 'Footnote'
//   nestedTitle.textContent = `${titleText}`
//   nestedTitle.style.cssText = `
//     margin: 0;
//     color: white;
//     font-size: 1.1rem;
//     font-weight: 600;
//     text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
//   `

//   // Create close button for nested popup
//   const nestedCloseButton = document.createElement('button')
//   nestedCloseButton.innerHTML = '×'
//   nestedCloseButton.style.cssText = `
//     background: rgba(255, 255, 255, 0.2);
//     border: 2px solid rgba(255, 255, 255, 0.3);
//     font-size: 28px;
//     cursor: pointer;
//     color: white;
//     padding: 0;
//     width: 36px;
//     height: 36px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 50%;
//     transition: all 0.2s;
//     backdrop-filter: blur(10px);
//   `

//   nestedCloseButton.addEventListener('mouseenter', () => {
//     nestedCloseButton.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
//     nestedCloseButton.style.transform = 'scale(1.1)'
//   })
//   nestedCloseButton.addEventListener('mouseleave', () => {
//     nestedCloseButton.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
//     nestedCloseButton.style.transform = 'scale(1)'
//   })

//   nestedHeader.appendChild(nestedTitle)
//   nestedHeader.appendChild(nestedCloseButton)

//   // Create scrollable content area for nested popup
//   const nestedContentContainer = document.createElement('div')
//   nestedContentContainer.className = 'nested-popup-content'
//   nestedContentContainer.style.cssText = `
//     padding: 20px;
//     overflow-y: auto;
//     flex: 1;
//     line-height: 1.6;
//     background: white;
//     color: #333;
//   `

//   // ✅ COPY ALL STYLESHEETS FROM MAIN DOCUMENT TO NESTED POPUP
//   const copyStylesToNestedPopup = () => {
//     // Copy all link tags (external CSS files)
//     const linkTags = document.querySelectorAll('link[rel="stylesheet"]')
//     linkTags.forEach(linkTag => {
//       if (linkTag.href && !nestedContentContainer.querySelector(`link[href="${linkTag.href}"]`)) {
//         const newLink = document.createElement('link')
//         newLink.rel = 'stylesheet'
//         newLink.href = linkTag.href
//         newLink.type = 'text/css'
//         nestedContentContainer.appendChild(newLink)
//       }
//     })

//     // Copy all style tags (inline CSS)
//     const styleTags = document.querySelectorAll('style')
//     styleTags.forEach(styleTag => {
//       if (styleTag.textContent) {
//         const newStyle = document.createElement('style')
//         newStyle.textContent = styleTag.textContent
//         nestedContentContainer.appendChild(newStyle)
//       }
//     })

//     // Copy external CSS that's specifically loaded for guides
//     const externalGuideCSS = document.getElementById('external-css')
//     if (externalGuideCSS && externalGuideCSS.href) {
//       const newLink = document.createElement('link')
//       newLink.rel = 'stylesheet'
//       newLink.href = externalGuideCSS.href
//       newLink.id = 'nested-external-css'
//       nestedContentContainer.appendChild(newLink)
//     }

//     // Also try to copy from document.styleSheets programmatically
//     try {
//       Array.from(document.styleSheets).forEach(sheet => {
//         if (sheet.href) {
//           // External stylesheet
//           if (!nestedContentContainer.querySelector(`link[href="${sheet.href}"]`)) {
//             const newLink = document.createElement('link')
//             newLink.rel = 'stylesheet'
//             newLink.href = sheet.href
//             newLink.type = 'text/css'
//             nestedContentContainer.appendChild(newLink)
//           }
//         } else {
//           // Inline stylesheet
//           try {
//             let cssText = ''
//             Array.from(sheet.cssRules).forEach(rule => {
//               cssText += rule.cssText + '\n'
//             })
//             if (cssText) {
//               const newStyle = document.createElement('style')
//               newStyle.textContent = cssText
//               nestedContentContainer.appendChild(newStyle)
//             }
//           } catch (e) {
//             console.warn('Could not access stylesheet rules:', e)
//           }
//         }
//       })
//     } catch (e) {
//       console.warn('Error copying stylesheets:', e)
//     }
//   }

//   // Store base URL for relative link resolution
//   nestedContentContainer.setAttribute(
//     'data-base-url',
//     baseUrl || window.location.pathname
//   )

//   // Add the full document content and find the specific section
//   if (fullDocumentContent) {
//     const tempContainer = document.createElement('div')
//     tempContainer.innerHTML = fullDocumentContent

//     // Find the target element
//     const targetElement = findElementById(tempContainer, targetId.substring(1))

//     if (targetElement) {
//       // Clone the target element and its content
//       const clonedElement = targetElement.cloneNode(true)

//       // ✅ COPY STYLES BEFORE ADDING CONTENT
//       copyStylesToNestedPopup()

//       nestedContentContainer.appendChild(clonedElement)

//       // Process links in the nested popup content
//       processLinksInContainer(
//         nestedContentContainer,
//         showSnackbar,
//         fullDocumentContent,
//         documentTitle,
//         baseUrl,
//         true // isNestedPopup flag
//       )
//     } else {
//       copyStylesToNestedPopup()
//       nestedContentContainer.innerHTML = `<p style="color: #666;">Content for section "${targetId}" not found.</p>`
//     }
//   } else {
//     copyStylesToNestedPopup()
//     nestedContentContainer.innerHTML = `<p style="color: #666;">Full document content not available.</p>`
//   }

//   nestedPopup.appendChild(nestedHeader)
//   nestedPopup.appendChild(nestedContentContainer)
//   nestedOverlay.appendChild(nestedPopup)
//   document.body.appendChild(nestedOverlay)

//   // Close nested popup handlers
//   const closeNestedPopup = () => {
//     document.removeEventListener('keydown', handleNestedEsc, true)

//     // Clean up CSS that was added for this nested popup
//     const nestedPopupCSS = document.querySelectorAll('[data-popup-css="nested"]')
//     nestedPopupCSS.forEach(element => element.remove())

//     nestedOverlay.remove()
//   }

//   nestedCloseButton.addEventListener('click', closeNestedPopup)
//   nestedOverlay.addEventListener('click', (e) => {
//     if (e.target === nestedOverlay) {
//       closeNestedPopup()
//     }
//   })

//   // ESC key to close nested popup (higher priority)
//   const handleNestedEsc = (e) => {
//     if (e.key === 'Escape') {
//       e.stopPropagation()
//       closeNestedPopup()
//     }
//   }
//   document.addEventListener('keydown', handleNestedEsc, true) // capture: true for higher priority

//   console.log(`Created nested popup for: ${targetId}`)

//   if (showSnackbar) {
//     showSnackbar(`${titleText} opened`, 'info')
//   }

//   return nestedPopup
// }

// // Create popup for internal content
// const createEnhancedPopup = (
//   targetId,
//   fullDocumentContent,
//   documentTitle,
//   showSnackbar,
//   baseUrl = null
// ) => {
//   // Remove existing popup if any
//   const existingPopup = document.getElementById('content-popup')
//   if (existingPopup) {
//     existingPopup.remove()
//   }

//   // Create popup overlay
//   const overlay = document.createElement('div')
//   overlay.id = 'content-popup'
//   overlay.style.cssText = `
//     position: fixed;
//     top: 0;
//     left: 0;
//     width: 100%;
//     height: 100%;
//     background-color: rgba(0, 0, 0, 0.7);
//     z-index: 10000;
//     display: flex;
//     justify-content: center;
//     align-items: center;
//     padding: 20px;
//     box-sizing: border-box;
//   `

//   // Create popup content container
//   const popup = document.createElement('div')
//   popup.style.cssText = `
//     background: white;
//     border-radius: 8px;
//     max-width: 90%;
//     max-height: 90%;
//     overflow: hidden;
//     position: relative;
//     box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
//     display: flex;
//     flex-direction: column;
//   `

//   // Create header with title and close button
//   const header = document.createElement('div')
//   header.style.cssText = `
//     padding: 16px 20px;
//     border-bottom: 1px solid #e0e0e0;
//     display: flex;
//     justify-content: space-between;
//     align-items: center;
//     background: #f5f5f5;
//     flex-shrink: 0;
//   `

//   const title = document.createElement('h3')
//   title.textContent =
//     documentTitle.replace(/<[^>]*>/g, '') || 'Document Section'
//   title.style.cssText = `
//     margin: 0;
//     color: #232536;
//     font-size: 1.1rem;
//     font-weight: 600;
//   `

//   // Create close button
//   const closeButton = document.createElement('button')
//   closeButton.innerHTML = '×'
//   closeButton.style.cssText = `
//     background: none;
//     border: none;
//     font-size: 28px;
//     cursor: pointer;
//     color: #666;
//     padding: 0;
//     width: 32px;
//     height: 32px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 4px;
//     transition: background-color 0.2s;
//   `

//   closeButton.addEventListener('mouseenter', () => {
//     closeButton.style.backgroundColor = '#e0e0e0'
//   })
//   closeButton.addEventListener('mouseleave', () => {
//     closeButton.style.backgroundColor = 'transparent'
//   })

//   header.appendChild(title)
//   header.appendChild(closeButton)

//   // Create scrollable content area
//   const contentContainer = document.createElement('div')
//   contentContainer.className = 'popup-content'
//   contentContainer.style.cssText = `
//     padding: 20px;
//     overflow-y: auto;
//     flex: 1;
//     line-height: 1.6;
//   `

//   // ✅ COPY ALL STYLESHEETS FROM MAIN DOCUMENT TO MAIN POPUP
//   const copyStylesToMainPopup = () => {
//     // Copy all link tags (external CSS files)
//     const linkTags = document.querySelectorAll('link[rel="stylesheet"]')
//     linkTags.forEach(linkTag => {
//       if (linkTag.href && !contentContainer.querySelector(`link[href="${linkTag.href}"]`)) {
//         const newLink = document.createElement('link')
//         newLink.rel = 'stylesheet'
//         newLink.href = linkTag.href
//         newLink.type = 'text/css'
//         contentContainer.appendChild(newLink)
//       }
//     })

//     // Copy all style tags (inline CSS)
//     const styleTags = document.querySelectorAll('style')
//     styleTags.forEach(styleTag => {
//       if (styleTag.textContent) {
//         const newStyle = document.createElement('style')
//         newStyle.textContent = styleTag.textContent
//         contentContainer.appendChild(newStyle)
//       }
//     })

//     // Copy external CSS that's specifically loaded for guides
//     const externalGuideCSS = document.getElementById('external-css')
//     if (externalGuideCSS && externalGuideCSS.href) {
//       const newLink = document.createElement('link')
//       newLink.rel = 'stylesheet'
//       newLink.href = externalGuideCSS.href
//       newLink.id = 'main-popup-external-css'
//       contentContainer.appendChild(newLink)
//     }

//     // Also try to copy from document.styleSheets programmatically
//     try {
//       Array.from(document.styleSheets).forEach(sheet => {
//         if (sheet.href) {
//           // External stylesheet
//           if (!contentContainer.querySelector(`link[href="${sheet.href}"]`)) {
//             const newLink = document.createElement('link')
//             newLink.rel = 'stylesheet'
//             newLink.href = sheet.href
//             newLink.type = 'text/css'
//             contentContainer.appendChild(newLink)
//           }
//         } else {
//           // Inline stylesheet
//           try {
//             let cssText = ''
//             Array.from(sheet.cssRules).forEach(rule => {
//               cssText += rule.cssText + '\n'
//             })
//             if (cssText) {
//               const newStyle = document.createElement('style')
//               newStyle.textContent = cssText
//               contentContainer.appendChild(newStyle)
//             }
//           } catch (e) {
//             console.warn('Could not access stylesheet rules:', e)
//           }
//         }
//       })
//     } catch (e) {
//       console.warn('Error copying stylesheets:', e)
//     }
//   }

//   // Store base URL for relative link resolution
//   contentContainer.setAttribute(
//     'data-base-url',
//     baseUrl || window.location.pathname
//   )

//   // Create back-to-top button container
//   const backToTopContainer = document.createElement('div')
//   backToTopContainer.style.cssText = `
//     position: absolute;
//     bottom: 20px;
//     right: 20px;
//     z-index: 10001;
//     pointer-events: none;
//   `

//   // Create back-to-top button
//   const backToTopButton = document.createElement('button')
//   backToTopButton.id = 'popup-back-to-top'

//   // Detect if mobile (simple check based on screen width)
//   const isMobile = window.innerWidth <= 768

//   if (isMobile) {
//     // Mobile: Small circular button
//     backToTopButton.innerHTML = '↑'
//     backToTopButton.style.cssText = `
//       width: 40px;
//       height: 40px;
//       border-radius: 50%;
//       background-color: #232536;
//       color: white;
//       border: none;
//       cursor: pointer;
//       font-size: 20px;
//       font-weight: bold;
//       display: flex;
//       align-items: center;
//       justify-content: center;
//       box-shadow: 0 2px 10px rgba(0,0,0,0.3);
//       transition: all 0.3s ease;
//       opacity: 0;
//       transform: scale(0);
//       pointer-events: auto;
//     `
//   } else {
//     // Desktop: Extended button with text
//     backToTopButton.innerHTML = '↑ Back to Top'
//     backToTopButton.style.cssText = `
//       padding: 8px 16px;
//       border-radius: 28px;
//       background-color: #232536;
//       color: white;
//       border: none;
//       cursor: pointer;
//       font-size: 14px;
//       font-weight: bold;
//       display: flex;
//       align-items: center;
//       justify-content: center;
//       box-shadow: 0 2px 10px rgba(0,0,0,0.3);
//       transition: all 0.3s ease;
//       opacity: 0;
//       transform: scale(0);
//       gap: 8px;
//       pointer-events: auto;
//     `
//   }

//   // Hover effects for back-to-top button
//   backToTopButton.addEventListener('mouseenter', () => {
//     if (backToTopButton.style.opacity === '1') {
//       backToTopButton.style.backgroundColor = '#3a3e5a'
//       const currentTransform = backToTopButton.style.transform
//       if (currentTransform.includes('scale(1)')) {
//         backToTopButton.style.transform = currentTransform.replace(
//           'scale(1)',
//           'scale(1.05)'
//         )
//       }
//     }
//   })
//   backToTopButton.addEventListener('mouseleave', () => {
//     if (backToTopButton.style.opacity === '1') {
//       backToTopButton.style.backgroundColor = '#232536'
//       const currentTransform = backToTopButton.style.transform
//       if (currentTransform.includes('scale(1.05)')) {
//         backToTopButton.style.transform = currentTransform.replace(
//           'scale(1.05)',
//           'scale(1)'
//         )
//       }
//     }
//   })

//   // Back-to-top functionality
//   backToTopButton.addEventListener('click', () => {
//     contentContainer.scrollTo({
//       top: 0,
//       behavior: 'smooth',
//     })
//   })

//   // Add button to container
//   backToTopContainer.appendChild(backToTopButton)

//   // Add the full document content with proper link handling
//   if (fullDocumentContent) {
//     // ✅ COPY STYLES FIRST BEFORE ADDING CONTENT
//     copyStylesToMainPopup()

//     // Create a temporary container to parse and process the content
//     const tempContainer = document.createElement('div')
//     tempContainer.innerHTML = fullDocumentContent

//     // Process all links in the content to add proper event handlers
//     processLinksInContainer(
//       tempContainer,
//       showSnackbar,
//       fullDocumentContent,
//       documentTitle,
//       baseUrl,
//       false // isNestedPopup flag
//     )

//     // Add the processed content to the popup
//     contentContainer.appendChild(tempContainer)
//   } else {
//     // ✅ COPY STYLES EVEN FOR FALLBACK CONTENT
//     copyStylesToMainPopup()

//     // Fallback: try to get content from current page
//     const mainContent =
//       document.querySelector('main') ||
//       document.querySelector('.article-content') ||
//       document.querySelector('.html-content') ||
//       document.body
//     if (mainContent) {
//       const tempContainer = document.createElement('div')
//       tempContainer.innerHTML = mainContent.innerHTML

//       // Process all links in the fallback content too
//       processLinksInContainer(
//         tempContainer,
//         showSnackbar,
//         mainContent.innerHTML,
//         documentTitle,
//         baseUrl,
//         false // isNestedPopup flag
//       )

//       contentContainer.appendChild(tempContainer)
//     } else {
//       contentContainer.innerHTML = `<p>Full document content not available. Section: ${targetId}</p>`
//     }
//   }

//   // Set up scroll detection for back-to-top button
//   const handleScroll = () => {
//     // Show/hide button based on scroll position
//     if (contentContainer.scrollTop > 300) {
//       backToTopButton.style.opacity = '1'
//       backToTopButton.style.transform = 'scale(1)'
//     } else {
//       backToTopButton.style.opacity = '0'
//       backToTopButton.style.transform = 'scale(0)'
//     }
//   }

//   // Add scroll listener
//   contentContainer.addEventListener('scroll', handleScroll)

//   popup.appendChild(header)
//   popup.appendChild(contentContainer)
//   popup.appendChild(backToTopContainer)
//   overlay.appendChild(popup)
//   document.body.appendChild(overlay)

//   // Close popup handlers
//   const closePopup = () => {
//     // Clean up scroll listener
//     contentContainer.removeEventListener('scroll', handleScroll)

//     // Clean up CSS that was added for this main popup
//     const mainPopupCSS = document.querySelectorAll('[data-popup-css="main"]')
//     mainPopupCSS.forEach(element => element.remove())

//     overlay.remove()
//   }

//   closeButton.addEventListener('click', closePopup)
//   overlay.addEventListener('click', (e) => {
//     if (e.target === overlay) {
//       closePopup()
//     }
//   })

//   // ESC key to close (only if no nested popup exists)
//   const handleEsc = (e) => {
//     if (e.key === 'Escape') {
//       // Check if nested popup exists, if so, don't close main popup
//       const nestedPopup = document.getElementById('nested-content-popup')
//       if (!nestedPopup) {
//         closePopup()
//         document.removeEventListener('keydown', handleEsc)
//       }
//     }
//   }
//   document.addEventListener('keydown', handleEsc)

//   // Scroll to target section after popup is rendered
//   setTimeout(() => {
//     // Clean the targetId (remove # if present)
//     const cleanTargetId = targetId.startsWith('#')
//       ? targetId.substring(1)
//       : targetId

//     // Try to find the target element using our robust finder
//     const targetElement = findElementById(contentContainer, cleanTargetId)

//     if (targetElement) {
//       targetElement.scrollIntoView({
//         behavior: 'smooth',
//         block: 'start',
//       })

//       // Highlight the target section briefly
//       const originalBackground = targetElement.style.backgroundColor
//       const originalTransition = targetElement.style.transition

//       targetElement.style.backgroundColor = '#fff3cd'
//       targetElement.style.transition = 'background-color 0.3s ease'

//       setTimeout(() => {
//         targetElement.style.backgroundColor = originalBackground
//         setTimeout(() => {
//           targetElement.style.transition = originalTransition
//         }, 300)
//       }, 2000)

//       console.log(`Successfully scrolled to and highlighted: ${cleanTargetId}`)

//       if (showSnackbar) {
//         showSnackbar(`Scrolled to section: ${cleanTargetId}`, 'success')
//       }
//     } else {
//       console.warn(`Target element not found: ${cleanTargetId}`)

//       if (showSnackbar) {
//         showSnackbar(
//           `Section "${cleanTargetId}" not found in document`,
//           'warning'
//         )
//       }

//       // Log available IDs for debugging
//       const elementsWithIds = contentContainer.querySelectorAll('[id]')
//       const availableIds = Array.from(elementsWithIds)
//         .map((el) => el.id)
//         .filter((id) => id)
//       console.log('Available IDs in document:', availableIds)
//     }
//   }, 100)

//   return popup
// }

// // ✅ UPDATED: Helper function to process all links in a container and add proper event handlers
// const processLinksInContainer = (
//   container,
//   showSnackbar,
//   fullDocumentContent,
//   documentTitle,
//   baseUrl,
//   isNestedPopup = false
// ) => {
//   const links = container.querySelectorAll('a[href]')

//   links.forEach((link) => {
//     const href = link.getAttribute('href')

//     if (!href) return

//     // Skip if link already has our custom handler
//     if (link.hasAttribute('data-processed')) return

//     // Mark as processed to avoid double-processing
//     link.setAttribute('data-processed', 'true')

//     // Remove any existing onclick handlers to avoid conflicts
//     link.onclick = null

//     // Add our custom click handler
//     link.addEventListener('click', (e) => {
//       // ✅ Check for special URL schemes BEFORE preventing default
//       if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('sms:')) {
//         console.log(`Utils: Allowing default behavior for special scheme: ${href}`)
//         // Don't prevent default - let browser handle it naturally
//         if (showSnackbar) {
//           const scheme = href.split(':')[0]
//           showSnackbar(`Opening ${scheme} link`, 'info')
//         }
//         return // Exit early, let browser handle the special scheme
//       }

//       // Only prevent default for links we want to handle ourselves
//       e.preventDefault()

//       const linkText = link.textContent?.trim() || 'Document'

//       // Handle different types of links
//       if (href.startsWith('#')) {
//         // ✅ Check if it's a special bookmark/note link
//         if (isSpecialBookmarkLink(href)) {
//           // Always create nested popup for special links
//           createNestedPopup(
//             href,
//             fullDocumentContent,
//             documentTitle,
//             showSnackbar,
//             baseUrl
//           )
//           return
//         }

//         // Internal anchor link - navigate within the same popup or create new popup
//         const targetId = href.substring(1)

//         // Check if we're inside a popup (regular or nested)
//         const popup =
//           link.closest('#content-popup') ||
//           link.closest('#nested-content-popup')
//         if (popup) {
//           // We're inside a popup - navigate within it
//           const contentContainer =
//             popup.querySelector('.popup-content') ||
//             popup.querySelector('.nested-popup-content') ||
//             popup.querySelector('div[style*="overflow-y: auto"]') ||
//             popup.querySelector('div:last-child')

//           if (contentContainer) {
//             // Find the target element within the popup content
//             const targetElement = findElementById(contentContainer, targetId)

//             if (targetElement) {
//               targetElement.scrollIntoView({
//                 behavior: 'smooth',
//                 block: 'start',
//               })

//               // Highlight the target section briefly
//               const originalBackground = targetElement.style.backgroundColor
//               const originalTransition = targetElement.style.transition

//               targetElement.style.backgroundColor = '#fff3cd'
//               targetElement.style.transition = 'background-color 0.3s ease'

//               setTimeout(() => {
//                 targetElement.style.backgroundColor = originalBackground
//                 setTimeout(() => {
//                   targetElement.style.transition = originalTransition
//                 }, 300)
//               }, 2000)

//               console.log(`Navigated within popup to: ${targetId}`)

//               if (showSnackbar) {
//                 showSnackbar(`Navigated to section: ${targetId}`, 'success')
//               }
//             } else {
//               console.warn(`Target element not found in popup: ${targetId}`)

//               // Log available IDs for debugging
//               const elementsWithIds = contentContainer.querySelectorAll('[id]')
//               const availableIds = Array.from(elementsWithIds)
//                 .map((el) => el.id)
//                 .filter((id) => id)
//               console.log('Available IDs in popup:', availableIds)

//               if (showSnackbar) {
//                 showSnackbar(`Section "${targetId}" not found`, 'warning')
//               }
//             }
//           } else {
//             console.warn('Could not find content container in popup')
//           }
//         } else {
//           // We're not in a popup - create a new popup
//           createEnhancedPopup(
//             targetId,
//             fullDocumentContent,
//             documentTitle,
//             showSnackbar,
//             baseUrl
//           )

//           if (showSnackbar) {
//             showSnackbar('Section opened in popup', 'info')
//           }
//         }
//       } else if (isExternalUrl(href)) {
//         // External link - use focus management
//         openUrlWithFocusManagement(href, linkText, showSnackbar)
//       } else {
//         // Internal document link (relative or absolute internal) - use focus management
//         let fullUrl

//         if (href.startsWith('http') || href.startsWith('//')) {
//           fullUrl = href
//         } else if (href.startsWith('/')) {
//           fullUrl = `${window.location.origin}${href}`
//         } else {
//           // Relative link - construct full URL using the base URL
//           const currentBaseUrl = baseUrl || window.location.pathname
//           const basePath = currentBaseUrl.substring(
//             0,
//             currentBaseUrl.lastIndexOf('/') + 1
//           )
//           fullUrl = `${window.location.origin}${basePath}${href}`
//         }

//         openUrlWithFocusManagement(fullUrl, linkText, showSnackbar)
//       }
//     })
//   })
// }

// // Main function to parse HTML and handle all links with enhanced popup support
// export const parseHtmlSanitizeAddTargetToLinks = (
//   htmlContent,
//   searchQuery = null,
//   baseUrl = null,
//   options = {}
// ) => {
//   if (!htmlContent) return null

//   const {
//     fullDocumentContent = null,
//     documentTitle = 'Document',
//     showSnackbar = null,
//   } = options

//   // Step 1: Sanitize HTML
//   const cleanHtml = DOMPurify.sanitize(htmlContent, {
//     ADD_TAGS: ['iframe', 'embed', 'video', 'audio', 'source'],
//     ADD_ATTR: [
//       'allowfullscreen',
//       'frameborder',
//       'scrolling',
//       'src',
//       'controls',
//       'autoplay',
//       'loop',
//       'muted',
//       'poster',
//       'preload',
//       'target',
//       'rel',
//     ],
//   })

//   // Step 2: Apply highlighting if search query is provided
//   const highlightedHtml = searchQuery
//     ? highlightText(cleanHtml, searchQuery)
//     : cleanHtml

//   // Step 3: Parse HTML and add link handlers
//   const parseOptions = {
//     replace: (domNode) => {
//       // Handle anchor tags
//       if (domNode.name === 'a' && domNode.attribs && domNode.attribs.href) {
//         const href = domNode.attribs.href
//         const linkText = domNode.children
//           ?.map((child) => parseChildNode(child))
//           ?.join('')

//         // Create new props for the link
//         const linkProps = {
//           ...domNode.attribs,
//           onClick: (e) => {
//             // ✅ Check for special URL schemes BEFORE preventing default
//             if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('sms:')) {
//               console.log(`Utils: Allowing default behavior for special scheme: ${href}`)
//               // Don't prevent default - let browser handle it naturally
//               if (showSnackbar) {
//                 const scheme = href.split(':')[0]
//                 showSnackbar(`Opening ${scheme} link`, 'info')
//               }
//               return // Exit early, let browser handle the special scheme
//             }

//             // Only prevent default for links we want to handle ourselves
//             e.preventDefault()

//             if (href.startsWith('#')) {
//               // ✅ Check if it's a special bookmark/note link that should create nested popup
//               if (isSpecialBookmarkLink(href)) {
//                 createNestedPopup(
//                   href,
//                   fullDocumentContent,
//                   documentTitle,
//                   showSnackbar,
//                   baseUrl
//                 )
//                 return
//               }

//               // Internal anchor link
//               const targetId = href.substring(1)

//               // Check if we're inside a popup
//               const popup = e.target.closest('#content-popup') || e.target.closest('#nested-content-popup')
//               if (popup) {
//                 // We're inside a popup - navigate within it
//                 const contentContainer =
//                   popup.querySelector('.popup-content') ||
//                   popup.querySelector('.nested-popup-content') ||
//                   popup.querySelector('div[style*="overflow-y: auto"]') ||
//                   popup.querySelector('div:last-child')

//                 if (contentContainer) {
//                   const targetElement = findElementById(contentContainer, targetId)

//                   if (targetElement) {
//                     targetElement.scrollIntoView({
//                       behavior: 'smooth',
//                       block: 'start',
//                     })

//                     // Highlight the target section briefly
//                     const originalBackground = targetElement.style.backgroundColor
//                     const originalTransition = targetElement.style.transition

//                     targetElement.style.backgroundColor = '#fff3cd'
//                     targetElement.style.transition = 'background-color 0.3s ease'

//                     setTimeout(() => {
//                       targetElement.style.backgroundColor = originalBackground
//                       setTimeout(() => {
//                         targetElement.style.transition = originalTransition
//                       }, 300)
//                     }, 2000)

//                     console.log(`Navigated within popup to: ${targetId}`)

//                     if (showSnackbar) {
//                       showSnackbar(`Navigated to section: ${targetId}`, 'success')
//                     }
//                   } else {
//                     console.warn(`Target element not found in popup: ${targetId}`)

//                     if (showSnackbar) {
//                       showSnackbar(`Section "${targetId}" not found`, 'warning')
//                     }
//                   }
//                 }
//               } else {
//                 // We're not in a popup - create a new popup
//                 createEnhancedPopup(
//                   targetId,
//                   fullDocumentContent,
//                   documentTitle,
//                   showSnackbar,
//                   baseUrl
//                 )

//                 if (showSnackbar) {
//                   showSnackbar('Section opened in popup', 'info')
//                 }
//               }
//             } else if (isExternalUrl(href)) {
//               // External link
//               openUrlWithFocusManagement(href, linkText, showSnackbar)
//             } else {
//               // Internal document link - construct full URL and use focus management
//               let fullUrl

//               if (href.startsWith('http') || href.startsWith('//')) {
//                 fullUrl = href
//               } else if (href.startsWith('/')) {
//                 fullUrl = `${window.location.origin}${href}`
//               } else {
//                 // Relative link
//                 const currentBaseUrl = baseUrl || window.location.pathname
//                 const basePath = currentBaseUrl.substring(
//                   0,
//                   currentBaseUrl.lastIndexOf('/') + 1
//                 )
//                 fullUrl = `${window.location.origin}${basePath}${href}`
//               }

//               openUrlWithFocusManagement(fullUrl, linkText, showSnackbar)
//             }
//           },
//         }

//         // Add target="_blank" and rel attributes for external links
//         if (isExternalUrl(href)) {
//           linkProps.target = '_blank'
//           linkProps.rel = 'noopener noreferrer'
//         }

//         // Return the enhanced anchor element
//         return React.createElement(
//           'a',
//           linkProps,
//           domNode.children?.map((child, index) => parseChildNode(child, index))
//         )
//       }

//       // Handle other elements normally
//       return undefined
//     },
//   }

//   return parse(highlightedHtml, parseOptions)
// }


// ----------------------------------------------------------------------------------------
// v2 Version 2 Fixes
// Dont copies CSS functionality from react component to head of index.html so that 2nd popup for gtl notes and footnotes have same look and feel
// Cleaner code structure without the CSS management complexity
// Simpler popup creation without head manipulation
// Better organized event handler cleanup

// import React, { useEffect } from 'react'
// import DOMPurify from 'dompurify'
// import parse from 'html-react-parser'
// import { openDocumentInTab, clearDocumentTabsTracking } from './tabManager'

// // Improved highlighting function
// export const highlightText = (text, searchQuery) => {
//   if (!searchQuery || typeof text !== 'string') return text

//   // Escape special characters in the search query
//   const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

//   // Create a regex that can match the search term
//   const regex = new RegExp(`(${escapedQuery})`, 'gi')

//   // Split HTML into parts inside tags and text content
//   const parts = []
//   let inTag = false
//   let currentPart = ''

//   for (let i = 0; i < text.length; i++) {
//     if (text[i] === '<') {
//       if (currentPart) {
//         parts.push({ isTag: inTag, content: currentPart })
//         currentPart = ''
//       }
//       inTag = true
//       currentPart += text[i]
//     } else if (text[i] === '>') {
//       currentPart += text[i]
//       parts.push({ isTag: inTag, content: currentPart })
//       currentPart = ''
//       inTag = false
//     } else {
//       currentPart += text[i]
//     }
//   }

//   if (currentPart) {
//     parts.push({ isTag: inTag, content: currentPart })
//   }

//   // Only highlight text parts, not tag parts
//   return parts
//     .map((part) => {
//       if (part.isTag) return part.content
//       return part.content.replace(regex, '<mark>$1</mark>')
//     })
//     .join('')
// }

// // Helper function to parse child nodes, especially handling <mark> tags
// const parseChildNode = (child, index) => {
//   if (child?.name === 'mark') {
//     return (
//       <mark key={index}>
//         {child?.children?.map((subChild) => subChild?.data).join('')}
//       </mark>
//     )
//   }
//   return child?.data || ''
// }

// // Helper function to check if a URL is absolute
// const isAbsoluteUrl = (url) => {
//   return /^(https?:\/\/|\/\/|[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,})/i.test(
//     url
//   )
// }

// // Helper function to check if URL is external
// const isExternalUrl = (url) => {
//   if (!url.startsWith('http')) return false
//   try {
//     const urlObj = new URL(url)
//     return urlObj.hostname !== window.location.hostname
//   } catch {
//     return false
//   }
// }

// // Helper function to check if link is a special bookmark/note link
// const isSpecialBookmarkLink = (href) => {
//   if (!href || !href.startsWith('#')) return false

//   const fragment = href.substring(1) // Remove the #

//   // Check for bookmark patterns (include #bookmark, #bookmark0, #bookmark1, etc.)
//   // But exclude #bookmarkback, #bookmarkback1, etc.
//   if (fragment.startsWith('bookmark')) {
//     // If it starts with 'bookmarkback', exclude it
//     if (fragment.startsWith('bookmarkback')) {
//       return false
//     }
//     // If it's exactly 'bookmark' or 'bookmark' followed by digits, include it
//     const afterBookmark = fragment.substring(8) // 'bookmark'.length = 8
//     if (afterBookmark === '' || /^\d+$/.test(afterBookmark)) {
//       return true
//     }
//   }

//   // Check for footnote patterns (include #footnote1, #footnote10, etc.)
//   // But exclude #footnoteback, #footnoteback1, etc.
//   if (fragment.startsWith('footnote')) {
//     // If it starts with 'footnoteback', exclude it
//     if (fragment.startsWith('footnoteback')) {
//       return false
//     }
//     // If it's 'footnote' followed by digits, include it
//     const afterFootnote = fragment.substring(8) // 'footnote'.length = 8
//     if (/^\d+$/.test(afterFootnote)) {
//       return true
//     }
//   }

//   // Check for gtlnotes patterns (include #gtlnotes0, #gtlnotes1, etc.)
//   // But exclude #gtlnotes (without numbers)
//   if (fragment.startsWith('gtlnotes')) {
//     const afterGtlnotes = fragment.substring(8) // 'gtlnotes'.length = 8
//     // Only include if followed by digits (exclude bare 'gtlnotes')
//     if (/^\d+$/.test(afterGtlnotes)) {
//       return true
//     }
//   }

//   return false
// }

// // Helper function to open URL in new tab with proper focus management
// const openUrlWithFocusManagement = (url, linkText, showSnackbar) => {
//   console.log(`Utils: Opening URL with focus management: ${url}`)
//   openDocumentInTab(url, linkText, showSnackbar)
// }

// // Enhanced CSS selector escaping function
// const escapeSelector = (selector) => {
//   return selector.replace(/[!"#$%&'()*+,.\/:;<=>?@\[\\\]^`{|}~]/g, '\\$&')
// }

// // Helper function to find element by ID with special characters
// const findElementById = (container, targetId) => {
//   // Method 1: Try direct getElementById (works in most cases)
//   let targetElement = container.querySelector
//     ? container.ownerDocument?.getElementById(targetId) ||
//       document.getElementById(targetId)
//     : document.getElementById(targetId)

//   if (
//     targetElement &&
//     container.contains &&
//     container.contains(targetElement)
//   ) {
//     return targetElement
//   }

//   // Method 2: Try escaped selector
//   try {
//     const escapedId = escapeSelector(targetId)
//     targetElement = container.querySelector(`#${escapedId}`)
//     if (targetElement) {
//       return targetElement
//     }
//   } catch (e) {
//     console.warn(
//       `Failed to query with escaped selector: #${escapeSelector(targetId)}`,
//       e
//     )
//   }

//   // Method 3: Use attribute selector (most reliable for special characters)
//   try {
//     targetElement = container.querySelector(`[id="${targetId}"]`)
//     if (targetElement) {
//       return targetElement
//     }
//   } catch (e) {
//     console.warn(
//       `Failed to query with attribute selector: [id="${targetId}"]`,
//       e
//     )
//   }

//   // Method 4: Manual search through all elements with IDs
//   const allElementsWithId = container.querySelectorAll('[id]')
//   for (const element of allElementsWithId) {
//     if (element.id === targetId) {
//       return element
//     }
//   }

//   // Method 5: Also check for name attribute (fallback for older HTML)
//   try {
//     targetElement = container.querySelector(`[name="${targetId}"]`)
//     if (targetElement) {
//       return targetElement
//     }
//   } catch (e) {
//     console.warn(`Failed to query with name attribute: [name="${targetId}"]`, e)
//   }

//   return null
// }

// // Create nested popup for special bookmarks/notes (appears on top of existing popup)
// const createNestedPopup = (
//   targetId,
//   fullDocumentContent,
//   documentTitle,
//   showSnackbar,
//   baseUrl = null
// ) => {
//   // Remove existing nested popup if any
//   const existingNestedPopup = document.getElementById('nested-content-popup')
//   if (existingNestedPopup) {
//     existingNestedPopup.remove()
//   }

//   // Create nested popup overlay with higher z-index
//   const nestedOverlay = document.createElement('div')
//   nestedOverlay.id = 'nested-content-popup'
//   nestedOverlay.style.cssText = `
//     position: fixed;
//     top: 0;
//     left: 0;
//     width: 100%;
//     height: 100%;
//     background-color: rgba(0, 0, 0, 0.8);
//     z-index: 15000;
//     display: flex;
//     justify-content: center;
//     align-items: center;
//     padding: 20px;
//     box-sizing: border-box;
//   `

//   // Create nested popup content container with distinct styling
//   const nestedPopup = document.createElement('div')
//   nestedPopup.style.cssText = `
//   background: #232536;
//   border-radius: 12px;
//   max-width: 85%;
//   max-height: 85%;
//   overflow: hidden;
//   position: relative;
//   box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
//   display: flex;
//   flex-direction: column;
//   border: 3px solid #fff;
// `

//   // Create header with distinct styling for nested popup
//   const nestedHeader = document.createElement('div')
//   nestedHeader.style.cssText = `
//   padding: 16px 20px;
//   border-bottom: 2px solid rgba(255, 255, 255, 0.2);
//   display: flex;
//   justify-content: space-between;
//   align-items: center;
//   background: rgba(255, 255, 255, 0.05);
//   backdrop-filter: blur(10px);
//   flex-shrink: 0;
// `

//   const nestedTitle = document.createElement('h3')
//   const titleText = targetId.startsWith('gtlNotes') ? 'GTL Notes' : 'Footnote'
//   nestedTitle.textContent = `${titleText}`
//   nestedTitle.style.cssText = `
//     margin: 0;
//     color: white;
//     font-size: 1.1rem;
//     font-weight: 600;
//     text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
//   `

//   // Create close button for nested popup
//   const nestedCloseButton = document.createElement('button')
//   nestedCloseButton.innerHTML = '×'
//   nestedCloseButton.style.cssText = `
//     background: rgba(255, 255, 255, 0.2);
//     border: 2px solid rgba(255, 255, 255, 0.3);
//     font-size: 28px;
//     cursor: pointer;
//     color: white;
//     padding: 0;
//     width: 36px;
//     height: 36px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 50%;
//     transition: all 0.2s;
//     backdrop-filter: blur(10px);
//   `

//   nestedCloseButton.addEventListener('mouseenter', () => {
//     nestedCloseButton.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
//     nestedCloseButton.style.transform = 'scale(1.1)'
//   })
//   nestedCloseButton.addEventListener('mouseleave', () => {
//     nestedCloseButton.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
//     nestedCloseButton.style.transform = 'scale(1)'
//   })

//   nestedHeader.appendChild(nestedTitle)
//   nestedHeader.appendChild(nestedCloseButton)

//   // Create scrollable content area for nested popup
//   const nestedContentContainer = document.createElement('div')
//   nestedContentContainer.className = 'nested-popup-content'
//   nestedContentContainer.style.cssText = `
//   padding: 20px;
//   overflow-y: auto;
//   flex: 1;
//   line-height: 1.6;
//   background: white;
//   color: #333;
// `

//   // Store base URL for relative link resolution
//   nestedContentContainer.setAttribute(
//     'data-base-url',
//     baseUrl || window.location.pathname
//   )

//   // Add the full document content and find the specific section
//   if (fullDocumentContent) {
//     const tempContainer = document.createElement('div')
//     tempContainer.innerHTML = fullDocumentContent

//     // Find the target element
//     const targetElement = findElementById(tempContainer, targetId.substring(1))

//     if (targetElement) {
//       // Clone the target element and its content
//       const clonedElement = targetElement.cloneNode(true)
//       nestedContentContainer.appendChild(clonedElement)

//       // Process links in the nested popup content
//       processLinksInContainer(
//         nestedContentContainer,
//         showSnackbar,
//         fullDocumentContent,
//         documentTitle,
//         baseUrl,
//         true // isNestedPopup flag
//       )
//     } else {
//       nestedContentContainer.innerHTML = `<p style="color: #666;">Content for section "${targetId}" not found.</p>`
//     }
//   } else {
//     nestedContentContainer.innerHTML = `<p style="color: #666;">Full document content not available.</p>`
//   }

//   nestedPopup.appendChild(nestedHeader)
//   nestedPopup.appendChild(nestedContentContainer)
//   nestedOverlay.appendChild(nestedPopup)
//   document.body.appendChild(nestedOverlay)

//   // Close nested popup handlers
//   const closeNestedPopup = () => {
//     document.removeEventListener('keydown', handleNestedEsc, true)
//     nestedOverlay.remove()
//   }

//   nestedCloseButton.addEventListener('click', closeNestedPopup)
//   nestedOverlay.addEventListener('click', (e) => {
//     if (e.target === nestedOverlay) {
//       closeNestedPopup()
//     }
//   })

//   // ESC key to close nested popup
//   const handleNestedEsc = (e) => {
//     if (e.key === 'Escape') {
//       e.stopPropagation()
//       closeNestedPopup()
//       document.removeEventListener('keydown', handleNestedEsc)
//     }
//   }
//   // capture: true for higher priority
//   document.addEventListener('keydown', handleNestedEsc, true)

//   console.log(`Created nested popup for: ${targetId}`)

//   if (showSnackbar) {
//     showSnackbar(`${titleText} opened`, 'info')
//   }

//   return nestedPopup
// }

// // Create popup for internal content
// const createEnhancedPopup = (
//   targetId,
//   fullDocumentContent,
//   documentTitle,
//   showSnackbar,
//   baseUrl = null
// ) => {
//   // Remove existing popup if any
//   const existingPopup = document.getElementById('content-popup')
//   if (existingPopup) {
//     existingPopup.remove()
//   }

//   // Create popup overlay
//   const overlay = document.createElement('div')
//   overlay.id = 'content-popup'
//   overlay.style.cssText = `
//     position: fixed;
//     top: 0;
//     left: 0;
//     width: 100%;
//     height: 100%;
//     background-color: rgba(0, 0, 0, 0.7);
//     z-index: 10000;
//     display: flex;
//     justify-content: center;
//     align-items: center;
//     padding: 20px;
//     box-sizing: border-box;
//   `

//   // Create popup content container
//   const popup = document.createElement('div')
//   popup.style.cssText = `
//     background: white;
//     border-radius: 8px;
//     max-width: 90%;
//     max-height: 90%;
//     overflow: hidden;
//     position: relative;
//     box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
//     display: flex;
//     flex-direction: column;
//   `

//   // Create header with title and close button
//   const header = document.createElement('div')
//   header.style.cssText = `
//     padding: 16px 20px;
//     border-bottom: 1px solid #e0e0e0;
//     display: flex;
//     justify-content: space-between;
//     align-items: center;
//     background: #f5f5f5;
//     flex-shrink: 0;
//   `

//   const title = document.createElement('h3')
//   title.textContent =
//     documentTitle.replace(/<[^>]*>/g, '') || 'Document Section'
//   title.style.cssText = `
//     margin: 0;
//     color: #232536;
//     font-size: 1.1rem;
//     font-weight: 600;
//   `

//   // Create close button
//   const closeButton = document.createElement('button')
//   closeButton.innerHTML = '×'
//   closeButton.style.cssText = `
//     background: none;
//     border: none;
//     font-size: 28px;
//     cursor: pointer;
//     color: #666;
//     padding: 0;
//     width: 32px;
//     height: 32px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 4px;
//     transition: background-color 0.2s;
//   `

//   closeButton.addEventListener('mouseenter', () => {
//     closeButton.style.backgroundColor = '#e0e0e0'
//   })
//   closeButton.addEventListener('mouseleave', () => {
//     closeButton.style.backgroundColor = 'transparent'
//   })

//   header.appendChild(title)
//   header.appendChild(closeButton)

//   // Create scrollable content area
//   const contentContainer = document.createElement('div')
//   contentContainer.className = 'popup-content'
//   contentContainer.style.cssText = `
//     padding: 20px;
//     overflow-y: auto;
//     flex: 1;
//     line-height: 1.6;
//   `

//   // Store base URL for relative link resolution
//   contentContainer.setAttribute(
//     'data-base-url',
//     baseUrl || window.location.pathname
//   )

//   // Create back-to-top button container
//   const backToTopContainer = document.createElement('div')
//   backToTopContainer.style.cssText = `
//     position: absolute;
//     bottom: 20px;
//     right: 20px;
//     z-index: 10001;
//     pointer-events: none;
//   `

//   // Create back-to-top button
//   const backToTopButton = document.createElement('button')
//   backToTopButton.id = 'popup-back-to-top'

//   // Detect if mobile (simple check based on screen width)
//   const isMobile = window.innerWidth <= 768

//   if (isMobile) {
//     // Mobile: Small circular button
//     backToTopButton.innerHTML = '↑'
//     backToTopButton.style.cssText = `
//       width: 40px;
//       height: 40px;
//       border-radius: 50%;
//       background-color: #232536;
//       color: white;
//       border: none;
//       cursor: pointer;
//       font-size: 20px;
//       font-weight: bold;
//       display: flex;
//       align-items: center;
//       justify-content: center;
//       box-shadow: 0 2px 10px rgba(0,0,0,0.3);
//       transition: all 0.3s ease;
//       opacity: 0;
//       transform: scale(0);
//       pointer-events: auto;
//     `
//   } else {
//     // Desktop: Extended button with text
//     backToTopButton.innerHTML = '↑ Back to Top'
//     backToTopButton.style.cssText = `
//       padding: 8px 16px;
//       border-radius: 28px;
//       background-color: #232536;
//       color: white;
//       border: none;
//       cursor: pointer;
//       font-size: 14px;
//       font-weight: bold;
//       display: flex;
//       align-items: center;
//       justify-content: center;
//       box-shadow: 0 2px 10px rgba(0,0,0,0.3);
//       transition: all 0.3s ease;
//       opacity: 0;
//       transform: scale(0);
//       gap: 8px;
//       pointer-events: auto;
//     `
//   }

//   // Hover effects for back-to-top button
//   backToTopButton.addEventListener('mouseenter', () => {
//     if (backToTopButton.style.opacity === '1') {
//       backToTopButton.style.backgroundColor = '#3a3e5a'
//       const currentTransform = backToTopButton.style.transform
//       if (currentTransform.includes('scale(1)')) {
//         backToTopButton.style.transform = currentTransform.replace(
//           'scale(1)',
//           'scale(1.05)'
//         )
//       }
//     }
//   })
//   backToTopButton.addEventListener('mouseleave', () => {
//     if (backToTopButton.style.opacity === '1') {
//       backToTopButton.style.backgroundColor = '#232536'
//       const currentTransform = backToTopButton.style.transform
//       if (currentTransform.includes('scale(1.05)')) {
//         backToTopButton.style.transform = currentTransform.replace(
//           'scale(1.05)',
//           'scale(1)'
//         )
//       }
//     }
//   })

//   // Back-to-top functionality
//   backToTopButton.addEventListener('click', () => {
//     contentContainer.scrollTo({
//       top: 0,
//       behavior: 'smooth',
//     })
//   })

//   // Add button to container
//   backToTopContainer.appendChild(backToTopButton)

//   // Add the full document content with proper link handling
//   if (fullDocumentContent) {
//     // Create a temporary container to parse and process the content
//     const tempContainer = document.createElement('div')
//     tempContainer.innerHTML = fullDocumentContent

//     // Process all links in the content to add proper event handlers
//     processLinksInContainer(
//       tempContainer,
//       showSnackbar,
//       fullDocumentContent,
//       documentTitle,
//       baseUrl,
//       false // isNestedPopup flag
//     )

//     // Add the processed content to the popup
//     contentContainer.appendChild(tempContainer)
//   } else {
//     // Fallback: try to get content from current page
//     const mainContent =
//       document.querySelector('main') ||
//       document.querySelector('.article-content') ||
//       document.querySelector('.html-content') ||
//       document.body
//     if (mainContent) {
//       const tempContainer = document.createElement('div')
//       tempContainer.innerHTML = mainContent.innerHTML

//       // Process all links in the fallback content too
//       processLinksInContainer(
//         tempContainer,
//         showSnackbar,
//         mainContent.innerHTML,
//         documentTitle,
//         baseUrl,
//         false // isNestedPopup flag
//       )

//       contentContainer.appendChild(tempContainer)
//     } else {
//       contentContainer.innerHTML = `<p>Full document content not available. Section: ${targetId}</p>`
//     }
//   }

//   // Set up scroll detection for back-to-top button
//   const handleScroll = () => {
//     // Show/hide button based on scroll position
//     if (contentContainer.scrollTop > 300) {
//       backToTopButton.style.opacity = '1'
//       backToTopButton.style.transform = 'scale(1)'
//     } else {
//       backToTopButton.style.opacity = '0'
//       backToTopButton.style.transform = 'scale(0)'
//     }
//   }

//   // Add scroll listener
//   contentContainer.addEventListener('scroll', handleScroll)

//   popup.appendChild(header)
//   popup.appendChild(contentContainer)
//   popup.appendChild(backToTopContainer)
//   overlay.appendChild(popup)
//   document.body.appendChild(overlay)

//   // Close popup handlers
//   const closePopup = () => {
//     // Clean up scroll listener
//     contentContainer.removeEventListener('scroll', handleScroll)
//     overlay.remove()
//   }

//   closeButton.addEventListener('click', closePopup)
//   overlay.addEventListener('click', (e) => {
//     if (e.target === overlay) {
//       closePopup()
//     }
//   })

//   // ESC key to close
//   const handleEsc = (e) => {
//     if (e.key === 'Escape') {
//       // Check if nested popup exists, if so, don't close main popup
//       const nestedPopup = document.getElementById('nested-content-popup')
//       if (!nestedPopup) {
//         closePopup()
//         document.removeEventListener('keydown', handleEsc)
//       }
//     }
//   }
//   document.addEventListener('keydown', handleEsc)

//   // Scroll to target section after popup is rendered
//   setTimeout(() => {
//     // Clean the targetId (remove # if present)
//     const cleanTargetId = targetId.startsWith('#')
//       ? targetId.substring(1)
//       : targetId

//     // Try to find the target element using our robust finder
//     const targetElement = findElementById(contentContainer, cleanTargetId)

//     if (targetElement) {
//       targetElement.scrollIntoView({
//         behavior: 'smooth',
//         block: 'start',
//       })

//       // Highlight the target section briefly
//       const originalBackground = targetElement.style.backgroundColor
//       const originalTransition = targetElement.style.transition

//       targetElement.style.backgroundColor = '#fff3cd'
//       targetElement.style.transition = 'background-color 0.3s ease'

//       setTimeout(() => {
//         targetElement.style.backgroundColor = originalBackground
//         setTimeout(() => {
//           targetElement.style.transition = originalTransition
//         }, 300)
//       }, 2000)

//       console.log(`Successfully scrolled to and highlighted: ${cleanTargetId}`)

//       if (showSnackbar) {
//         showSnackbar(`Scrolled to section: ${cleanTargetId}`, 'success')
//       }
//     } else {
//       console.warn(`Target element not found: ${cleanTargetId}`)

//       if (showSnackbar) {
//         showSnackbar(
//           `Section "${cleanTargetId}" not found in document`,
//           'warning'
//         )
//       }

//       // Log available IDs for debugging
//       const elementsWithIds = contentContainer.querySelectorAll('[id]')
//       const availableIds = Array.from(elementsWithIds)
//         .map((el) => el.id)
//         .filter((id) => id)
//       console.log('Available IDs in document:', availableIds)
//     }
//   }, 100)

//   return popup
// }

// // Helper function to process all links in a container and add proper event handlers
// const processLinksInContainer = (
//   container,
//   showSnackbar,
//   fullDocumentContent,
//   documentTitle,
//   baseUrl,
//   isNestedPopup = false
// ) => {
//   const links = container.querySelectorAll('a[href]')

//   links.forEach((link) => {
//     const href = link.getAttribute('href')

//     if (!href) return

//     // Skip if link already has our custom handler
//     if (link.hasAttribute('data-processed')) return

//     // Mark as processed to avoid double-processing
//     link.setAttribute('data-processed', 'true')

//     // Remove any existing onclick handlers to avoid conflicts
//     link.onclick = null

//     // Add our custom click handler
//     link.addEventListener('click', (e) => {
//       if (
//         href.startsWith('mailto:') ||
//         href.startsWith('tel:') ||
//         href.startsWith('sms:')
//       ) {
//         console.log(
//           `Utils: Allowing default behavior for special scheme: ${href}`
//         )
//         // Don't prevent default - let browser handle it naturally
//         if (showSnackbar) {
//           const scheme = href.split(':')[0]
//           showSnackbar(`Opening ${scheme} link`, 'info')
//         }
//         return // Exit early, let browser handle the special scheme
//       }
//       e.preventDefault()

//       const linkText = link.textContent?.trim() || 'Document'

//       // Handle different types of links
//       if (href.startsWith('#')) {
//         // Check if it's a special bookmark/note link
//         if (isSpecialBookmarkLink(href)) {
//           // Always create nested popup for special links
//           createNestedPopup(
//             href,
//             fullDocumentContent,
//             documentTitle,
//             showSnackbar,
//             baseUrl
//           )
//           return
//         }

//         // Internal anchor link - navigate within the same popup or create new popup
//         const targetId = href.substring(1)

//         // Check if we're inside a popup (regular or nested)
//         const popup =
//           link.closest('#content-popup') ||
//           link.closest('#nested-content-popup')
//         if (popup) {
//           // We're inside a popup - navigate within it
//           const contentContainer =
//             popup.querySelector('.popup-content') ||
//             popup.querySelector('.nested-popup-content') ||
//             popup.querySelector('div[style*="overflow-y: auto"]') ||
//             popup.querySelector('div:last-child')

//           if (contentContainer) {
//             // Find the target element within the popup content
//             const targetElement = findElementById(contentContainer, targetId)

//             if (targetElement) {
//               targetElement.scrollIntoView({
//                 behavior: 'smooth',
//                 block: 'start',
//               })

//               // Highlight the target section briefly
//               const originalBackground = targetElement.style.backgroundColor
//               const originalTransition = targetElement.style.transition

//               targetElement.style.backgroundColor = '#fff3cd'
//               targetElement.style.transition = 'background-color 0.3s ease'

//               setTimeout(() => {
//                 targetElement.style.backgroundColor = originalBackground
//                 setTimeout(() => {
//                   targetElement.style.transition = originalTransition
//                 }, 300)
//               }, 2000)

//               console.log(`Navigated within popup to: ${targetId}`)

//               if (showSnackbar) {
//                 showSnackbar(`Navigated to section: ${targetId}`, 'success')
//               }
//             } else {
//               console.warn(`Target element not found in popup: ${targetId}`)

//               // Log available IDs for debugging
//               const elementsWithIds = contentContainer.querySelectorAll('[id]')
//               const availableIds = Array.from(elementsWithIds)
//                 .map((el) => el.id)
//                 .filter((id) => id)
//               console.log('Available IDs in popup:', availableIds)

//               if (showSnackbar) {
//                 showSnackbar(`Section "${targetId}" not found`, 'warning')
//               }
//             }
//           } else {
//             console.warn('Could not find content container in popup')
//           }
//         } else {
//           // We're not in a popup - create a new popup
//           createEnhancedPopup(
//             targetId,
//             fullDocumentContent,
//             documentTitle,
//             showSnackbar,
//             baseUrl
//           )

//           if (showSnackbar) {
//             showSnackbar('Section opened in popup', 'info')
//           }
//         }
//       } else if (isExternalUrl(href)) {
//         // External link - use focus management
//         openUrlWithFocusManagement(href, linkText, showSnackbar)
//       } else {
//         // Internal document link (relative or absolute internal) - use focus management
//         let fullUrl

//         if (href.startsWith('http') || href.startsWith('//')) {
//           fullUrl = href
//         } else if (href.startsWith('/')) {
//           fullUrl = `${window.location.origin}${href}`
//         } else {
//           // Relative link - construct full URL using the base URL
//           const currentBaseUrl = baseUrl || window.location.pathname
//           const basePath = currentBaseUrl.substring(
//             0,
//             currentBaseUrl.lastIndexOf('/') + 1
//           )
//           fullUrl = `${window.location.origin}${basePath}${href}`
//         }

//         openUrlWithFocusManagement(fullUrl, linkText, showSnackbar)
//       }
//     })
//   })
// }

// // Handle internal links that should open as popup
// const handleInternalLink = (
//   domNode,
//   fullDocumentContent,
//   documentTitle,
//   showSnackbar,
//   baseUrl
// ) => {
//   const href = domNode.attribs.href

//   return (
//     <a
//       href={href}
//       className={domNode.attribs.class}
//       id={domNode.attribs.id}
//       onClick={(e) => {
//         e.preventDefault()

//         if (href.startsWith('#')) {
//           // Check if it's a special bookmark/note link
//           if (isSpecialBookmarkLink(href)) {
//             createNestedPopup(
//               href,
//               fullDocumentContent,
//               documentTitle,
//               showSnackbar,
//               baseUrl
//             )
//             console.log(`Opened nested popup for special link: ${href}`)
//             return
//           }

//           const targetId = href.substring(1) // Remove #
//           createEnhancedPopup(
//             targetId,
//             fullDocumentContent,
//             documentTitle,
//             showSnackbar,
//             baseUrl
//           )
//           console.log(`Opened enhanced popup for: ${targetId}`)

//           if (showSnackbar) {
//             showSnackbar('Section opened in popup', 'info')
//           }
//         }
//       }}
//     >
//       {domNode.children?.map((child, i) => parseChildNode(child, i))}
//     </a>
//   )
// }

// // Handle links to different documents (new tab with focus management)
// const handleDocumentLink = (domNode, baseUrl, showSnackbar) => {
//   const href = domNode.attribs.href
//   const linkText =
//     domNode.children?.map((child) => child.data || '').join('') || 'Document'

//   return (
//     <a
//       href={href}
//       className={domNode.attribs.class}
//       id={domNode.attribs.id}
//       onClick={(e) => {
//         e.preventDefault()

//         let fullUrl

//         if (href.startsWith('http') || href.startsWith('//')) {
//           fullUrl = href
//         } else if (href.startsWith('/')) {
//           fullUrl = `${window.location.origin}${href}`
//         } else if (baseUrl) {
//           // Handle relative URLs with base URL
//           fullUrl = `${window.location.origin}${baseUrl}${href}`
//         } else {
//           fullUrl = `${window.location.origin}${window.location.pathname}${href}`
//         }

//         openUrlWithFocusManagement(fullUrl, linkText, showSnackbar)
//         console.log(`Opening document with focus management: ${fullUrl}`)
//       }}
//     >
//       {domNode.children?.map((child, i) => parseChildNode(child, i))}
//     </a>
//   )
// }

// // Handle external links (new tab with focus management)
// const handleExternalLink = (domNode, showSnackbar) => {
//   const href = domNode.attribs.href
//   const linkText =
//     domNode.children?.map((child) => child.data || '').join('') ||
//     'External Link'

//   return (
//     <a
//       href={href}
//       className={domNode.attribs.class}
//       id={domNode.attribs.id}
//       onClick={(e) => {
//         e.preventDefault()
//         openUrlWithFocusManagement(href, linkText, showSnackbar)
//         console.log(`Opening external link with focus management: ${href}`)
//       }}
//     >
//       {domNode.children?.map((child, i) => parseChildNode(child, i))}
//     </a>
//   )
// }

// /**
//  * React component to render HTML content with proper link handling
//  */
// const HtmlContent = ({
//   content,
//   searchQuery,
//   baseUrl,
//   fullDocumentContent,
//   documentTitle,
//   showSnackbar,
// }) => {
//   // Apply highlighting to the content if searchQuery is provided
//   const highlightedContent = searchQuery
//     ? highlightText(content, searchQuery)
//     : content

//   // Handle hash fragment scrolling when the page loads
//   useEffect(() => {
//     if (typeof window !== 'undefined' && window.location.hash) {
//       // Small delay to ensure DOM is fully rendered
//       const timer = setTimeout(() => {
//         const hash = window.location.hash
//         if (hash.startsWith('#')) {
//           const targetId = hash.substring(1)

//           // Use our robust element finder
//           const targetElement = findElementById(document, targetId)

//           if (targetElement) {
//             targetElement.scrollIntoView({ behavior: 'smooth' })
//             console.log(`Scrolled to element: ${targetId}`)
//           } else {
//             console.warn(`Target element not found: ${targetId}`)
//           }
//         }
//       }, 300)

//       return () => clearTimeout(timer)
//     }
//   }, [])

//   // Allow target, rel, class, id, name, and href attributes during sanitization
//   const cleanHtml = DOMPurify.sanitize(highlightedContent, {
//     ADD_ATTR: ['target', 'rel', 'class', 'id', 'name', 'href'],
//     ADD_TAGS: ['mark'], // Explicitly allow mark tags
//   })

//   // Parse the sanitized HTML
//   const parsedContent = parse(cleanHtml, {
//     replace: (domNode) => {
//       if (!domNode || !domNode.name) return

//       // Convert <a name="bookmarkX"> to use id attribute instead
//       if (
//         domNode.name === 'a' &&
//         domNode.attribs?.name &&
//         !domNode.attribs.id
//       ) {
//         return (
//           <a id={domNode.attribs.name}>
//             {domNode.children?.map((child, i) => parseChildNode(child, i))}
//           </a>
//         )
//       }

//       // Handle any link with a href attribute
//       if (domNode.name === 'a' && domNode.attribs?.href) {
//         const href = domNode.attribs.href
//         if (
//           href.startsWith('mailto:') ||
//           href.startsWith('tel:') ||
//           href.startsWith('sms:')
//         ) {
//           // Return normal anchor tag with default behavior
//           return (
//             <a
//               href={href}
//               className={domNode.attribs.class}
//               id={domNode.attribs.id}
//               onClick={(e) => {
//                 // Don't prevent default - let browser handle special schemes
//                 console.log(
//                   `HTML Parser: Allowing default behavior for: ${href}`
//                 )
//                 if (showSnackbar) {
//                   const scheme = href.split(':')[0]
//                   showSnackbar(`Opening ${scheme} link`, 'info')
//                 }
//               }}
//             >
//               {domNode.children?.map((child, i) => parseChildNode(child, i))}
//             </a>
//           )
//         }

//         // Check if it's an external URL
//         if (isExternalUrl(href)) {
//           return handleExternalLink(domNode, showSnackbar)
//         }

//         // Check if it's an absolute URL (but internal)
//         if (href.startsWith('http') || href.startsWith('//')) {
//           // Internal absolute URL - treat as document link
//           return handleDocumentLink(domNode, baseUrl, showSnackbar)
//         }

//         // Handle relative paths (not starting with #)
//         if (href.startsWith('/')) {
//           return handleDocumentLink(domNode, baseUrl, showSnackbar)
//         }

//         // Handle hash fragments - these are internal links
//         if (href.startsWith('#')) {
//           // All hash fragments are treated as internal links (popup or nested popup)
//           return handleInternalLink(
//             domNode,
//             fullDocumentContent,
//             documentTitle,
//             showSnackbar,
//             baseUrl
//           )
//         }

//         // Any other relative URLs - treat as document links
//         return handleDocumentLink(domNode, baseUrl, showSnackbar)
//       }

//       // Default behavior for all other elements
//       return undefined // Let the parser handle it
//     },
//   })

//   return <div className='html-content'>{parsedContent}</div>
// }

// /**
//  * Main export function that returns the HtmlContent component
//  */
// export const parseHtmlSanitizeAddTargetToLinks = (
//   content,
//   searchQuery,
//   baseUrl,
//   options = {}
// ) => {
//   if (!content) return null

//   const {
//     fullDocumentContent = null,
//     documentTitle = 'Document',
//     showSnackbar = null,
//   } = options

//   return (
//     <HtmlContent
//       content={content}
//       searchQuery={searchQuery}
//       baseUrl={baseUrl}
//       fullDocumentContent={fullDocumentContent}
//       documentTitle={documentTitle}
//       showSnackbar={showSnackbar}
//     />
//   )
// }

// // Maintain existing utility exports
// export const deepCopy = (obj) => {
//   return JSON.parse(JSON.stringify(obj))
// }

// export const debounce = (func, delay) => {
//   let timeoutId
//   return (...args) => {
//     if (timeoutId) {
//       clearTimeout(timeoutId)
//     }
//     timeoutId = setTimeout(() => {
//       func(...args)
//     }, delay)
//   }
// }

// // Clean up function to clear opened tabs tracking (uses shared tracking from articleCardHandlers)
// export const clearOpenedTabsTracking = () => {
//   clearDocumentTabsTracking()
// }


// ---------------------------------------------------------------------------------------------
// v1 Version 1
// open any new document in new tab always, scrolls up/down if user clicks on #bookmarkSection (no pop up)


// import React, { useEffect } from 'react'
// import DOMPurify from 'dompurify'
// import parse from 'html-react-parser'
// import { openDocumentInTab, clearDocumentTabsTracking } from './tabManager'

// // Improved highlighting function
// export const highlightText = (text, searchQuery) => {
//   if (!searchQuery || typeof text !== 'string') return text

//   // Escape special characters in the search query
//   const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

//   // Create a regex that can match the search term
//   const regex = new RegExp(`(${escapedQuery})`, 'gi')

//   // Split HTML into parts inside tags and text content
//   const parts = []
//   let inTag = false
//   let currentPart = ''

//   for (let i = 0; i < text.length; i++) {
//     if (text[i] === '<') {
//       if (currentPart) {
//         parts.push({ isTag: inTag, content: currentPart })
//         currentPart = ''
//       }
//       inTag = true
//       currentPart += text[i]
//     } else if (text[i] === '>') {
//       currentPart += text[i]
//       parts.push({ isTag: inTag, content: currentPart })
//       currentPart = ''
//       inTag = false
//     } else {
//       currentPart += text[i]
//     }
//   }

//   if (currentPart) {
//     parts.push({ isTag: inTag, content: currentPart })
//   }

//   // Only highlight text parts, not tag parts
//   return parts
//     .map((part) => {
//       if (part.isTag) return part.content
//       return part.content.replace(regex, '<mark>$1</mark>')
//     })
//     .join('')
// }

// // Helper function to parse child nodes, especially handling <mark> tags
// const parseChildNode = (child, index) => {
//   if (child?.name === 'mark') {
//     return (
//       <mark key={index}>
//         {child?.children?.map((subChild) => subChild?.data).join('')}
//       </mark>
//     )
//   }
//   return child?.data || ''
// }

// // Helper function to check if a URL is absolute
// const isAbsoluteUrl = (url) => {
//   return /^(https?:\/\/|\/\/|[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,})/i.test(
//     url
//   )
// }

// // Helper function to check if URL is external
// const isExternalUrl = (url) => {
//   if (!url.startsWith('http')) return false
//   try {
//     const urlObj = new URL(url)
//     return urlObj.hostname !== window.location.hostname
//   } catch {
//     return false
//   }
// }

// // Helper function to open URL in new tab with proper focus management
// const openUrlWithFocusManagement = (url, linkText, showSnackbar) => {
//   // Use the shared tab manager which handles:
//   // - Base URL tracking (without hash fragments)
//   // - Checking if document is already open
//   // - Focusing existing tab and navigating to hash section
//   // - Opening new tab only when needed
//   // - Proper cleanup of closed tabs
//   console.log(`Utils: Opening URL with focus management: ${url}`)
//   openDocumentInTab(url, linkText, showSnackbar)
// }

// // Helper function to process all links in a container and add proper event handlers
// const processLinksInContainer = (
//   container,
//   showSnackbar,
//   fullDocumentContent,
//   documentTitle,
//   baseUrl
// ) => {
//   const links = container.querySelectorAll('a[href]')

//   links.forEach((link) => {
//     const href = link.getAttribute('href')

//     if (!href) return

//     // Skip if link already has our custom handler
//     if (link.hasAttribute('data-processed')) return

//     // Mark as processed to avoid double-processing
//     link.setAttribute('data-processed', 'true')

//     // Remove any existing onclick handlers to avoid conflicts
//     link.onclick = null

//     // Add our custom click handler
//     link.addEventListener('click', (e) => {
//       if (
//         href.startsWith('mailto:') ||
//         href.startsWith('tel:') ||
//         href.startsWith('sms:')
//       ) {
//         console.log(
//           `Utils: Allowing default behavior for special scheme: ${href}`
//         )
//         // Don't prevent default - let browser handle it naturally
//         if (showSnackbar) {
//           const scheme = href.split(':')[0]
//           showSnackbar(`Opening ${scheme} link`, 'info')
//         }
//         return // Exit early, let browser handle the special scheme
//       }
//       e.preventDefault()

//       const linkText = link.textContent?.trim() || 'Document'

//       // Handle different types of links
//       if (href.startsWith('#')) {
//         // Internal anchor link - navigate within the same popup
//         const targetId = href.substring(1)

//         // Check if we're inside a popup
//         const popup = link.closest('#content-popup')
//         if (popup) {
//           // We're inside a popup - navigate within it
//           const contentContainer =
//             popup.querySelector('.popup-content') ||
//             popup.querySelector('div[style*="overflow-y: auto"]') ||
//             popup.querySelector('div:last-child')

//           if (contentContainer) {
//             // Find the target element within the popup content
//             const targetElement = findElementById(contentContainer, targetId)

//             if (targetElement) {
//               targetElement.scrollIntoView({
//                 behavior: 'smooth',
//                 block: 'start',
//               })

//               // Highlight the target section briefly
//               const originalBackground = targetElement.style.backgroundColor
//               const originalTransition = targetElement.style.transition

//               targetElement.style.backgroundColor = '#fff3cd'
//               targetElement.style.transition = 'background-color 0.3s ease'

//               setTimeout(() => {
//                 targetElement.style.backgroundColor = originalBackground
//                 setTimeout(() => {
//                   targetElement.style.transition = originalTransition
//                 }, 300)
//               }, 2000)

//               console.log(`Navigated within popup to: ${targetId}`)

//               if (showSnackbar) {
//                 showSnackbar(`Navigated to section: ${targetId}`, 'success')
//               }
//             } else {
//               console.warn(`Target element not found in popup: ${targetId}`)

//               // Log available IDs for debugging
//               const elementsWithIds = contentContainer.querySelectorAll('[id]')
//               const availableIds = Array.from(elementsWithIds)
//                 .map((el) => el.id)
//                 .filter((id) => id)
//               console.log('Available IDs in popup:', availableIds)

//               if (showSnackbar) {
//                 showSnackbar(`Section "${targetId}" not found`, 'warning')
//               }
//             }
//           } else {
//             console.warn('Could not find content container in popup')
//           }
//         } else {
//           // We're not in a popup - create a new popup
//           createEnhancedPopup(
//             targetId,
//             fullDocumentContent,
//             documentTitle,
//             showSnackbar,
//             baseUrl
//           )

//           if (showSnackbar) {
//             showSnackbar('Section opened in popup', 'info')
//           }
//         }
//       } else if (isExternalUrl(href)) {
//         // External link - use focus management
//         openUrlWithFocusManagement(href, linkText, showSnackbar)
//       } else {
//         // Internal document link (relative or absolute internal) - use focus management
//         let fullUrl

//         if (href.startsWith('http') || href.startsWith('//')) {
//           fullUrl = href
//         } else if (href.startsWith('/')) {
//           fullUrl = `${window.location.origin}${href}`
//         } else {
//           // Relative link - construct full URL using the base URL
//           const currentBaseUrl = baseUrl || window.location.pathname
//           const basePath = currentBaseUrl.substring(
//             0,
//             currentBaseUrl.lastIndexOf('/') + 1
//           )
//           fullUrl = `${window.location.origin}${basePath}${href}`
//         }

//         openUrlWithFocusManagement(fullUrl, linkText, showSnackbar)
//       }
//     })
//   })
// }

// // Enhanced CSS selector escaping function
// const escapeSelector = (selector) => {
//   // Escape special characters that have meaning in CSS selectors
//   return selector.replace(/[!"#$%&'()*+,.\/:;<=>?@\[\\\]^`{|}~]/g, '\\$&')
// }

// // Helper function to find element by ID with special characters
// const findElementById = (container, targetId) => {
//   // Method 1: Try direct getElementById (works in most cases)
//   let targetElement = container.querySelector
//     ? container.ownerDocument?.getElementById(targetId) ||
//       document.getElementById(targetId)
//     : document.getElementById(targetId)

//   if (
//     targetElement &&
//     container.contains &&
//     container.contains(targetElement)
//   ) {
//     return targetElement
//   }

//   // Method 2: Try escaped selector
//   try {
//     const escapedId = escapeSelector(targetId)
//     targetElement = container.querySelector(`#${escapedId}`)
//     if (targetElement) {
//       return targetElement
//     }
//   } catch (e) {
//     console.warn(
//       `Failed to query with escaped selector: #${escapeSelector(targetId)}`,
//       e
//     )
//   }

//   // Method 3: Use attribute selector (most reliable for special characters)
//   try {
//     targetElement = container.querySelector(`[id="${targetId}"]`)
//     if (targetElement) {
//       return targetElement
//     }
//   } catch (e) {
//     console.warn(
//       `Failed to query with attribute selector: [id="${targetId}"]`,
//       e
//     )
//   }

//   // Method 4: Manual search through all elements with IDs
//   const allElementsWithId = container.querySelectorAll('[id]')
//   for (const element of allElementsWithId) {
//     if (element.id === targetId) {
//       return element
//     }
//   }

//   // Method 5: Also check for name attribute (fallback for older HTML)
//   try {
//     targetElement = container.querySelector(`[name="${targetId}"]`)
//     if (targetElement) {
//       return targetElement
//     }
//   } catch (e) {
//     console.warn(`Failed to query with name attribute: [name="${targetId}"]`, e)
//   }

//   return null
// }

// // Create popup for internal content
// const createEnhancedPopup = (
//   targetId,
//   fullDocumentContent,
//   documentTitle,
//   showSnackbar,
//   baseUrl = null
// ) => {
//   // Remove existing popup if any
//   const existingPopup = document.getElementById('content-popup')
//   if (existingPopup) {
//     existingPopup.remove()
//   }

//   // Create popup overlay
//   const overlay = document.createElement('div')
//   overlay.id = 'content-popup'
//   overlay.style.cssText = `
//     position: fixed;
//     top: 0;
//     left: 0;
//     width: 100%;
//     height: 100%;
//     background-color: rgba(0, 0, 0, 0.7);
//     z-index: 10000;
//     display: flex;
//     justify-content: center;
//     align-items: center;
//     padding: 20px;
//     box-sizing: border-box;
//   `

//   // Create popup content container
//   const popup = document.createElement('div')
//   popup.style.cssText = `
//     background: white;
//     border-radius: 8px;
//     max-width: 90%;
//     max-height: 90%;
//     overflow: hidden;
//     position: relative;
//     box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
//     display: flex;
//     flex-direction: column;
//   `

//   // Create header with title and close button
//   const header = document.createElement('div')
//   header.style.cssText = `
//     padding: 16px 20px;
//     border-bottom: 1px solid #e0e0e0;
//     display: flex;
//     justify-content: space-between;
//     align-items: center;
//     background: #f5f5f5;
//     flex-shrink: 0;
//   `

//   const title = document.createElement('h3')
//   title.textContent =
//     documentTitle.replace(/<[^>]*>/g, '') || 'Document Section'
//   title.style.cssText = `
//     margin: 0;
//     color: #232536;
//     font-size: 1.1rem;
//     font-weight: 600;
//   `

//   // Create close button
//   const closeButton = document.createElement('button')
//   closeButton.innerHTML = '×'
//   closeButton.style.cssText = `
//     background: none;
//     border: none;
//     font-size: 28px;
//     cursor: pointer;
//     color: #666;
//     padding: 0;
//     width: 32px;
//     height: 32px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 4px;
//     transition: background-color 0.2s;
//   `

//   closeButton.addEventListener('mouseenter', () => {
//     closeButton.style.backgroundColor = '#e0e0e0'
//   })
//   closeButton.addEventListener('mouseleave', () => {
//     closeButton.style.backgroundColor = 'transparent'
//   })

//   header.appendChild(title)
//   header.appendChild(closeButton)

//   // Create scrollable content area
//   const contentContainer = document.createElement('div')
//   contentContainer.className = 'popup-content'
//   contentContainer.style.cssText = `
//     padding: 20px;
//     overflow-y: auto;
//     flex: 1;
//     line-height: 1.6;
//   `

//   // Store base URL for relative link resolution
//   contentContainer.setAttribute(
//     'data-base-url',
//     baseUrl || window.location.pathname
//   )

//   // Create back-to-top button container
//   const backToTopContainer = document.createElement('div')
//   backToTopContainer.style.cssText = `
//     position: absolute;
//     bottom: 20px;
//     right: 20px;
//     z-index: 10001;
//     pointer-events: none;
//   `

//   // Create back-to-top button
//   const backToTopButton = document.createElement('button')
//   backToTopButton.id = 'popup-back-to-top'

//   // Detect if mobile (simple check based on screen width)
//   const isMobile = window.innerWidth <= 768

//   if (isMobile) {
//     // Mobile: Small circular button
//     backToTopButton.innerHTML = '↑'
//     backToTopButton.style.cssText = `
//       width: 40px;
//       height: 40px;
//       border-radius: 50%;
//       background-color: #232536;
//       color: white;
//       border: none;
//       cursor: pointer;
//       font-size: 20px;
//       font-weight: bold;
//       display: flex;
//       align-items: center;
//       justify-content: center;
//       box-shadow: 0 2px 10px rgba(0,0,0,0.3);
//       transition: all 0.3s ease;
//       opacity: 0;
//       transform: scale(0);
//       pointer-events: auto;
//     `
//   } else {
//     // Desktop: Extended button with text
//     backToTopButton.innerHTML = '↑ Back to Top'
//     backToTopButton.style.cssText = `
//       padding: 8px 16px;
//       border-radius: 28px;
//       background-color: #232536;
//       color: white;
//       border: none;
//       cursor: pointer;
//       font-size: 14px;
//       font-weight: bold;
//       display: flex;
//       align-items: center;
//       justify-content: center;
//       box-shadow: 0 2px 10px rgba(0,0,0,0.3);
//       transition: all 0.3s ease;
//       opacity: 0;
//       transform: scale(0);
//       gap: 8px;
//       pointer-events: auto;
//     `
//   }

//   // Hover effects for back-to-top button
//   backToTopButton.addEventListener('mouseenter', () => {
//     if (backToTopButton.style.opacity === '1') {
//       backToTopButton.style.backgroundColor = '#3a3e5a'
//       const currentTransform = backToTopButton.style.transform
//       if (currentTransform.includes('scale(1)')) {
//         backToTopButton.style.transform = currentTransform.replace(
//           'scale(1)',
//           'scale(1.05)'
//         )
//       }
//     }
//   })
//   backToTopButton.addEventListener('mouseleave', () => {
//     if (backToTopButton.style.opacity === '1') {
//       backToTopButton.style.backgroundColor = '#232536'
//       const currentTransform = backToTopButton.style.transform
//       if (currentTransform.includes('scale(1.05)')) {
//         backToTopButton.style.transform = currentTransform.replace(
//           'scale(1.05)',
//           'scale(1)'
//         )
//       }
//     }
//   })

//   // Back-to-top functionality
//   backToTopButton.addEventListener('click', () => {
//     contentContainer.scrollTo({
//       top: 0,
//       behavior: 'smooth',
//     })
//   })

//   // Add button to container
//   backToTopContainer.appendChild(backToTopButton)

//   // Add the full document content with proper link handling
//   if (fullDocumentContent) {
//     // Create a temporary container to parse and process the content
//     const tempContainer = document.createElement('div')
//     tempContainer.innerHTML = fullDocumentContent

//     // Process all links in the content to add proper event handlers
//     processLinksInContainer(
//       tempContainer,
//       showSnackbar,
//       fullDocumentContent,
//       documentTitle,
//       baseUrl
//     )

//     // Add the processed content to the popup
//     contentContainer.appendChild(tempContainer)
//   } else {
//     // Fallback: try to get content from current page
//     const mainContent =
//       document.querySelector('main') ||
//       document.querySelector('.article-content') ||
//       document.querySelector('.html-content') ||
//       document.body
//     if (mainContent) {
//       const tempContainer = document.createElement('div')
//       tempContainer.innerHTML = mainContent.innerHTML

//       // Process all links in the fallback content too
//       processLinksInContainer(
//         tempContainer,
//         showSnackbar,
//         mainContent.innerHTML,
//         documentTitle,
//         baseUrl
//       )

//       contentContainer.appendChild(tempContainer)
//     } else {
//       contentContainer.innerHTML = `<p>Full document content not available. Section: ${targetId}</p>`
//     }
//   }

//   // Set up scroll detection for back-to-top button
//   const handleScroll = () => {
//     // Show/hide button based on scroll position
//     if (contentContainer.scrollTop > 300) {
//       backToTopButton.style.opacity = '1'
//       backToTopButton.style.transform = 'scale(1)'
//     } else {
//       backToTopButton.style.opacity = '0'
//       backToTopButton.style.transform = 'scale(0)'
//     }
//   }

//   // Add scroll listener
//   contentContainer.addEventListener('scroll', handleScroll)

//   popup.appendChild(header)
//   popup.appendChild(contentContainer)
//   popup.appendChild(backToTopContainer)
//   overlay.appendChild(popup)
//   document.body.appendChild(overlay)

//   // Close popup handlers
//   const closePopup = () => {
//     // Clean up scroll listener
//     contentContainer.removeEventListener('scroll', handleScroll)
//     overlay.remove()
//   }

//   closeButton.addEventListener('click', closePopup)
//   overlay.addEventListener('click', (e) => {
//     if (e.target === overlay) {
//       closePopup()
//     }
//   })

//   // ESC key to close
//   const handleEsc = (e) => {
//     if (e.key === 'Escape') {
//       closePopup()
//       document.removeEventListener('keydown', handleEsc)
//     }
//   }
//   document.addEventListener('keydown', handleEsc)

//   // Scroll to target section after popup is rendered
//   setTimeout(() => {
//     // Clean the targetId (remove # if present)
//     const cleanTargetId = targetId.startsWith('#')
//       ? targetId.substring(1)
//       : targetId

//     // Try to find the target element using our robust finder
//     const targetElement = findElementById(contentContainer, cleanTargetId)

//     if (targetElement) {
//       targetElement.scrollIntoView({
//         behavior: 'smooth',
//         block: 'start',
//       })

//       // Highlight the target section briefly
//       const originalBackground = targetElement.style.backgroundColor
//       const originalTransition = targetElement.style.transition

//       targetElement.style.backgroundColor = '#fff3cd'
//       targetElement.style.transition = 'background-color 0.3s ease'

//       setTimeout(() => {
//         targetElement.style.backgroundColor = originalBackground
//         setTimeout(() => {
//           targetElement.style.transition = originalTransition
//         }, 300)
//       }, 2000)

//       console.log(`Successfully scrolled to and highlighted: ${cleanTargetId}`)

//       if (showSnackbar) {
//         showSnackbar(`Scrolled to section: ${cleanTargetId}`, 'success')
//       }
//     } else {
//       console.warn(`Target element not found: ${cleanTargetId}`)

//       if (showSnackbar) {
//         showSnackbar(
//           `Section "${cleanTargetId}" not found in document`,
//           'warning'
//         )
//       }

//       // Log available IDs for debugging
//       const elementsWithIds = contentContainer.querySelectorAll('[id]')
//       const availableIds = Array.from(elementsWithIds)
//         .map((el) => el.id)
//         .filter((id) => id)
//       console.log('Available IDs in document:', availableIds)
//     }
//   }, 100)

//   return popup
// }

// // Handle internal links that should open as popup
// const handleInternalLink = (
//   domNode,
//   fullDocumentContent,
//   documentTitle,
//   showSnackbar,
//   baseUrl
// ) => {
//   const href = domNode.attribs.href

//   return (
//     <a
//       href={href}
//       className={domNode.attribs.class}
//       id={domNode.attribs.id}
//       onClick={(e) => {
//         e.preventDefault()

//         if (href.startsWith('#')) {
//           const targetId = href.substring(1) // Remove #
//           createEnhancedPopup(
//             targetId,
//             fullDocumentContent,
//             documentTitle,
//             showSnackbar,
//             baseUrl
//           )
//           console.log(`Opened enhanced popup for: ${targetId}`)

//           if (showSnackbar) {
//             showSnackbar('Section opened in popup', 'info')
//           }
//         }
//       }}
//     >
//       {domNode.children?.map((child, i) => parseChildNode(child, i))}
//     </a>
//   )
// }

// // Handle links to different documents (new tab with focus management)
// const handleDocumentLink = (domNode, baseUrl, showSnackbar) => {
//   const href = domNode.attribs.href
//   const linkText =
//     domNode.children?.map((child) => child.data || '').join('') || 'Document'

//   return (
//     <a
//       href={href}
//       className={domNode.attribs.class}
//       id={domNode.attribs.id}
//       onClick={(e) => {
//         e.preventDefault()

//         let fullUrl

//         if (href.startsWith('http') || href.startsWith('//')) {
//           fullUrl = href
//         } else if (href.startsWith('/')) {
//           fullUrl = `${window.location.origin}${href}`
//         } else if (baseUrl) {
//           // Handle relative URLs with base URL
//           fullUrl = `${window.location.origin}${baseUrl}${href}`
//         } else {
//           fullUrl = `${window.location.origin}${window.location.pathname}${href}`
//         }

//         openUrlWithFocusManagement(fullUrl, linkText, showSnackbar)
//         console.log(`Opening document with focus management: ${fullUrl}`)
//       }}
//     >
//       {domNode.children?.map((child, i) => parseChildNode(child, i))}
//     </a>
//   )
// }

// // Handle external links (new tab with focus management)
// const handleExternalLink = (domNode, showSnackbar) => {
//   const href = domNode.attribs.href
//   const linkText =
//     domNode.children?.map((child) => child.data || '').join('') ||
//     'External Link'

//   return (
//     <a
//       href={href}
//       className={domNode.attribs.class}
//       id={domNode.attribs.id}
//       onClick={(e) => {
//         e.preventDefault()
//         openUrlWithFocusManagement(href, linkText, showSnackbar)
//         console.log(`Opening external link with focus management: ${href}`)
//       }}
//     >
//       {domNode.children?.map((child, i) => parseChildNode(child, i))}
//     </a>
//   )
// }

// /**
//  * React component to render HTML content with proper link handling
//  */
// const HtmlContent = ({
//   content,
//   searchQuery,
//   baseUrl,
//   fullDocumentContent,
//   documentTitle,
//   showSnackbar,
// }) => {
//   // Apply highlighting to the content if searchQuery is provided
//   const highlightedContent = searchQuery
//     ? highlightText(content, searchQuery)
//     : content

//   // Handle hash fragment scrolling when the page loads
//   useEffect(() => {
//     if (typeof window !== 'undefined' && window.location.hash) {
//       // Small delay to ensure DOM is fully rendered
//       const timer = setTimeout(() => {
//         const hash = window.location.hash
//         if (hash.startsWith('#')) {
//           const targetId = hash.substring(1)

//           // Use our robust element finder
//           const targetElement = findElementById(document, targetId)

//           if (targetElement) {
//             targetElement.scrollIntoView({ behavior: 'smooth' })
//             console.log(`Scrolled to element: ${targetId}`)
//           } else {
//             console.warn(`Target element not found: ${targetId}`)
//           }
//         }
//       }, 300)

//       return () => clearTimeout(timer)
//     }
//   }, [])

//   // Allow target, rel, class, id, name, and href attributes during sanitization
//   const cleanHtml = DOMPurify.sanitize(highlightedContent, {
//     ADD_ATTR: ['target', 'rel', 'class', 'id', 'name', 'href'],
//     ADD_TAGS: ['mark'], // Explicitly allow mark tags
//   })

//   // Parse the sanitized HTML
//   const parsedContent = parse(cleanHtml, {
//     replace: (domNode) => {
//       if (!domNode || !domNode.name) return

//       // Convert <a name="bookmarkX"> to use id attribute instead
//       if (
//         domNode.name === 'a' &&
//         domNode.attribs?.name &&
//         !domNode.attribs.id
//       ) {
//         return (
//           <a id={domNode.attribs.name}>
//             {domNode.children?.map((child, i) => parseChildNode(child, i))}
//           </a>
//         )
//       }

//       // Handle any link with a href attribute
//       if (domNode.name === 'a' && domNode.attribs?.href) {
//         const href = domNode.attribs.href
// if (
//   href.startsWith('mailto:') ||
//   href.startsWith('tel:') ||
//   href.startsWith('sms:')
// ) {
//   // Return normal anchor tag with default behavior
//   return (
//     <a
//       href={href}
//       className={domNode.attribs.class}
//       id={domNode.attribs.id}
//       onClick={(e) => {
//         // Don't prevent default - let browser handle special schemes
//         console.log(`HTML Parser: Allowing default behavior for: ${href}`)
//         if (showSnackbar) {
//           const scheme = href.split(':')[0]
//           showSnackbar(`Opening ${scheme} link`, 'info')
//         }
//       }}
//     >
//       {domNode.children?.map((child, i) => parseChildNode(child, i))}
//     </a>
//   )
// }
//         // Check if it's an external URL
//         if (isExternalUrl(href)) {
//           return handleExternalLink(domNode, showSnackbar)
//         }

//         // Check if it's an absolute URL (but internal)
//         if (href.startsWith('http') || href.startsWith('//')) {
//           // Internal absolute URL - treat as document link
//           return handleDocumentLink(domNode, baseUrl, showSnackbar)
//         }

//         // Handle relative paths (not starting with #)
//         if (href.startsWith('/')) {
//           return handleDocumentLink(domNode, baseUrl, showSnackbar)
//         }

//         // Handle hash fragments - these are internal links
//         if (href.startsWith('#')) {
//           // All hash fragments are treated as internal links (popup)
//           return handleInternalLink(
//             domNode,
//             fullDocumentContent,
//             documentTitle,
//             showSnackbar,
//             baseUrl
//           )
//         }

//         // Any other relative URLs - treat as document links
//         return handleDocumentLink(domNode, baseUrl, showSnackbar)
//       }

//       // Default behavior for all other elements
//       return undefined // Let the parser handle it
//     },
//   })

//   return <div className='html-content'>{parsedContent}</div>
// }

// /**
//  * Main export function that returns the HtmlContent component
//  */
// export const parseHtmlSanitizeAddTargetToLinks = (
//   content,
//   searchQuery,
//   baseUrl,
//   options = {}
// ) => {
//   if (!content) return null

//   const {
//     fullDocumentContent = null,
//     documentTitle = 'Document',
//     showSnackbar = null,
//   } = options

//   return (
//     <HtmlContent
//       content={content}
//       searchQuery={searchQuery}
//       baseUrl={baseUrl}
//       fullDocumentContent={fullDocumentContent}
//       documentTitle={documentTitle}
//       showSnackbar={showSnackbar}
//     />
//   )
// }

// // Maintain existing utility exports
// export const deepCopy = (obj) => {
//   return JSON.parse(JSON.stringify(obj))
// }

// export const debounce = (func, delay) => {
//   let timeoutId
//   return (...args) => {
//     if (timeoutId) {
//       clearTimeout(timeoutId)
//     }
//     timeoutId = setTimeout(() => {
//       func(...args)
//     }, delay)
//   }
// }

// // Clean up function to clear opened tabs tracking (uses shared tracking from articleCardHandlers)
// export const clearOpenedTabsTracking = () => {
//   clearDocumentTabsTracking()
// }